/**
 * @fileoverview Main Application Component
 * 
 * This is the root component of the dashboard application that integrates various visualization
 * components and manages the global state. The application follows a data-driven architecture
 * where data flows from the API through hooks to individual components.
 * 
 * Data Flow:
 * 1. API Data (from useMapData, useEventData hooks)
 * 2. State Management (React useState for UI state)
 * 3. Component Distribution (data passed as props)
 * 
 * Key Features:
 * - Interactive Map Visualization
 * - Time-based Event Analysis
 * - KPI Tracking
 * - Event List Management
 * 
 * @requires React
 * @requires bootstrap
 */

import React, { useState, useRef, useEffect, useMemo, useCallback, startTransition } from 'react';



import { useLocation, useParams, Routes, Route, Navigate } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import { AuthProvider } from './context/AuthContext';
import { SnackbarProvider } from './shared/contexts/SnackbarContext';
import { eventBus, EVENTS } from './shared/utils/eventBus';
import './App.css';
import useThemeNavigate from './hooks/useThemeNavigate';
import debounce from 'lodash/debounce';
import LandingPage from './components/LandingPage';
import OrganizationDashboard from './components/OrganizationDashboard';
import InvitationAcceptance from './components/InvitationAcceptance';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import BrandGuidelines from './components/BrandGuidelines';
import { useTheme } from './context/ThemeContext';
import { OrganizationThemeGenerator } from './themes/organizationThemes';
import { loadOrganizationFont } from './themes/fontLoader';
import ProtectedRoute from './components/ProtectedRoute';

// Import react-resizable-panels components
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import './PanelStyles.css'; // We'll create this file for panel styling

import Header from './components/layout/Header';
import KPIComponent from './components/KPIComponent/KPIComponent';
import EventListComponent from './components/EventListComponent';
import PanelErrorBoundary from './components/PanelErrorBoundary';
import AnalysisComponent from './components/AnalysisComponent/AnalysisComponent';
import MapComponent from './components/Map/Map';
import TimeChart from './components/TimeChart/TimeChart';
import InteractionChart from './components/InteractionChart/InteractionChart';
import DtmLayer from './components/Map/Layers/DtmLayer';
import DtmTrendsChart from './components/Charts/DtmTrendsChart';
import EmbedPage from './components/EmbedPage';
import ChatWizard from './components/ChatWizard/ChatWizard';
import { parseISO } from 'date-fns';
import { formatValue, formatButtonValue } from './components/KPIComponent/formatUtils';
import { useMapData } from './hooks/useMapData';
import { useEventData } from './hooks/useEventData';
import { useKPIData } from './hooks/useKPIData';
import { useACLEDData } from './hooks/useACLEDData';
import { useAcledHeatmap } from './hooks/useAcledHeatmap';
import { useEventFilters } from './hooks/useEventFilters';
import { useAnalysisData } from './hooks/useAnalysisData';
import { 
  Box, 
  Typography,
  AppBar,
  Toolbar,
  ButtonGroup,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  IconButton
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';

/**
 * API URL configuration
 * 
 * @type {string}
 */
const API_URL = '';  // Use relative URLs

const enableProfile = new URLSearchParams(window.location.search).get('profile') === '1';
const profileOnRender = globalThis.__REACT_PROFILE_ON_RENDER__ || (() => {});

// Helper to resolve a view from URL param against the API views list
const resolveViewFromParam = (views, viewParam) => {
  if (!viewParam || !Array.isArray(views)) return null;
  const paramLower = viewParam.toLowerCase();
  const paramUpper = viewParam.toUpperCase();
  // Aliases and friendly slugs
  const alias = {
    drcongo: 'COD',
    congo: 'COD',
    myanmar: 'MMR',
    burma: 'MMR',
    syria: 'SYR',
  };
  const aliasIso3 = alias[paramLower];
  // 1) Exact ISO3 match
  let found = views.find(c => (c.iso3 || '').toUpperCase() === paramUpper);
  if (found) return found;
  // 2) Match by alternate ISO3 alias
  if (aliasIso3) {
    found = views.find(c => (c.iso3 || '').toUpperCase() === aliasIso3);
    if (found) return found;
  }
  // 3) Match by code field if present
  found = views.find(c => (c.code || '').toLowerCase() === paramLower);
  if (found) return found;
  // 4) Fuzzy match by name contains
  found = views.find(c => (c.name || '').toLowerCase().includes(paramLower));
  return found || null;
};

/**
 * LocationDropdown component
 * 
 * Handles location selection and updates the global state accordingly
 * 
 * @param {object} props
 * @param {object} props.selectedViewData - Currently selected view data
 * @param {function} props.onLocationSelect - Callback for location selection
 * @returns {JSX.Element}
 */
const LocationDropdown = React.memo(({ selectedViewData, onLocationSelect }) => {
  const [adm1Data, setAdm1Data] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch admin1 locations when view changes
  useEffect(() => {
    const fetchAdmin1Data = async () => {
      if (!selectedViewData?.iso3) return;
      
      try {
        const apiUrl = `${API_URL}/api/admin-locations/${selectedViewData.iso3}/admin1`;
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error('Failed to fetch admin1 data');
        const data = await response.json();
        
        if (!Array.isArray(data)) {
          console.error('Expected array of locations, got:', typeof data);
          return;
        }

        const viewKey = selectedViewData.iso3;
        
        setAdm1Data(prevData => {
          const newData = {
            ...prevData,
            [viewKey]: data.map(location => ({
              id: location.id,
              name: location.name,
              pcode: location.pcode,
              lat: location.lat || 0,
              lon: location.lon || 0,
              zoom_extent: location.zoom_extent,
              center: location.center,
              zoom: location.zoom,
              bbox: location.bbox,
              type: location.type || 'admin1',
              parent_id: location.parent_id,
              view_code: selectedViewData.iso3
            }))
          };
          return newData;
        });
      } catch (error) {
        console.error('Error fetching admin1 data:', error);
        setAdm1Data(prevData => ({
          ...prevData,
          [selectedViewData.iso3]: []
        }));
      }
    };

    // Reset selection when view changes
    setSelectedLocation('');
    fetchAdmin1Data();
  }, [selectedViewData]); // Only depend on selectedViewData

  const locations = useMemo(() => {
    if (!selectedViewData?.iso3) return [];
    const viewLocations = adm1Data[selectedViewData.iso3] || [];
    return viewLocations
      .filter(loc => loc && loc.name) // Filter out null/undefined locations or names
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [adm1Data, selectedViewData]);

  const handleChange = useCallback((event) => {
    const selectedValue = event.target.value;
    if (selectedValue === '') {
      setSelectedLocation('');
      onLocationSelect(null);
    } else {
      const selectedLoc = locations.find(loc => loc.name === selectedValue);
      setSelectedLocation(selectedValue);
      
      if (selectedLoc) {
        onLocationSelect(selectedLoc);
      }
    }
  }, [locations, onLocationSelect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="dropdown location-dropdown" ref={dropdownRef}>
      <button 
        className={`btn-common dropdown-toggle ${selectedLocation ? 'has-value' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}
      >
        {selectedLocation ? (
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: '4px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedLocation}
            </span>
            <span style={{
              backgroundColor: 'rgba(0, 0, 0, 0.041)',
              color: 'rgba(0, 0, 0, 0.6)',
              padding: '1px 4px',
              borderRadius: '3px',
              fontSize: 'var(--font-size-md)',
              fontWeight: '400',
              flexShrink: 0
            }}>
              {locations.find(loc => loc.name === selectedLocation)?.pcode}
            </span>
          </div>
        ) : 'Select Location'}
        {selectedLocation && (
          <span
            className="clear-button"
            onClick={(e) => {
              e.stopPropagation();
              handleChange({ target: { value: '' } });
              setIsOpen(false);
            }}
            title="Clear selection"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleChange({ target: { value: '' } });
                setIsOpen(false);
              }
            }}
          >
            ×
          </span>
        )}
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {locations.length === 0 ? (
            <div className="dropdown-item no-results">No locations available</div>
          ) : (
            locations.map((loc, index) => (
              <button
                key={loc.pcode}
                className={`btn-common dropdown-item ${selectedLocation === loc.name ? 'active' : ''}`}
                onClick={() => {
                  setSelectedLocation(loc.name);
                  setIsOpen(false);
                  onLocationSelect(loc);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {loc.name}
                </span>
                <span style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.041)',
                  color: 'rgba(0, 0, 0, 0.16)',
                  padding: '1px 0px',
                  borderRadius: '3px',
                  fontSize: 'var(--font-size-md)',
                  fontWeight: '400',
                  flexShrink: 0
                }}>
                  {loc.pcode}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => 
  prevProps.selectedViewData?.iso3 === nextProps.selectedViewData?.iso3);

LocationDropdown.displayName = 'LocationDropdown';

// --- Cookie Helper Functions ---

// Simple cookie writing function
function setCookie(name, value, days = 365) { // Default to 1 year persistence
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  // Ensure the value is properly encoded
  const encodedValue = encodeURIComponent(JSON.stringify(value));
  // Added SameSite=Lax for security
  document.cookie = name + "=" + (encodedValue || "") + expires + "; path=/; SameSite=Lax"; 
}

// Simple cookie reading function
function getCookie(name) {
  if (typeof document === 'undefined') return null; // Avoid errors during SSR
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        // Decode and parse the value
        const parsedValue = JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
        return parsedValue;
      } catch (e) {
        console.error("Error parsing cookie:", name, e);
        return null;
      }
    }
  }
  return null;
}

// --- End Cookie Helper Functions ---

// Create a custom resize handle component
const ResizeHandle = ({ className = '', onDraggingChange, ...props }) => {
  // Use the onDragging prop from PanelResizeHandle
  const handleDraggingChange = (isDragging) => {
    document.body.classList.toggle('resizing', isDragging);
    if (onDraggingChange) {
      onDraggingChange(isDragging);
    }
  };
  
  return (
    <PanelResizeHandle
      className={`panel-resize-handle ${className}`}
      onDragging={handleDraggingChange} // Use the correct prop
      {...props}
    />
  );
};

/**
 * Main Application Component
 * 
 * Manages the global state and integrates various visualization components
 * 
 * @returns {JSX.Element}
 */
const AppContent = () => {
  const location = useLocation();
  const { view } = useParams();
  const [urlParams, setUrlParams] = useState(new URLSearchParams(location.search));
  const showHeaderParam = urlParams.get('showHeader');
  const forceView = urlParams.get('force') === 'true';
  const showHeader = showHeaderParam !== 'false';
  const isLandingPage = location.pathname === '/';
  
  // Debug lifecycle
  useEffect(() => {
    console.log('🚀 [AppContent] Mounted', { 
      path: location.pathname, 
      search: location.search,
      timestamp: Date.now() 
    });
    return () => console.log('🛑 [AppContent] Unmounted');
  }, []);

  const hasInitialized = useRef(false);
  const iframeSettingsTimeoutRef = useRef(null);
  const lastOrgIframeContextRef = useRef(null);
  const orgSlugRef = useRef(null);
  const { changeTheme } = useTheme();

  const normalizeUploadUrl = useCallback((value) => {
    if (!value || typeof value !== 'string') return value;
    try {
      const u = new URL(value, window.location.origin);
      if (u.pathname.startsWith('/uploads/')) {
        return `${u.pathname}${u.search || ''}`;
      }
      return value;
    } catch (_) {
      return value;
    }
  }, []);

  const normalizeBranding = useCallback((branding) => {
    if (!branding || typeof branding !== 'object') return branding;
    const next = { ...branding };
    if (typeof next.logo === 'string') next.logo = normalizeUploadUrl(next.logo);
    if (typeof next.headerLogoUrl === 'string') next.headerLogoUrl = normalizeUploadUrl(next.headerLogoUrl);
    return next;
  }, [normalizeUploadUrl]);

  // Listen for URL parameter updates AND settings updates from parent organization dashboard
  useEffect(() => {
    let timeoutId;
    
    const handleMessage = (event) => {
      if (event.data.type === 'UPDATE_URL_PARAMS') {
        const newParams = event.data.params;
        console.log('📡 Received URL parameter update:', newParams);
        
        // Clear any pending updates to prevent rapid-fire updates
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        // Debounce the actual update to prevent DOM race conditions
        timeoutId = setTimeout(() => {
          // Check if parameters actually changed to avoid unnecessary updates
          const currentParams = new URLSearchParams(location.search);
          const newParamsObj = new URLSearchParams(newParams);
          
          // Compare parameter strings to see if anything changed
          if (currentParams.toString() !== newParamsObj.toString()) {
            // Update URL without reload
            const currentPath = location.pathname;
            const newUrl = `${currentPath}${newParams}`;
            
            // Update browser URL
            window.history.replaceState({}, '', newUrl);
            
            // Use startTransition for all updates - layout is now responsive to showHeader
            startTransition(() => {
              setUrlParams(newParamsObj);
            });
            
            console.log('✅ Updated URL parameters without reload');
          } else {
            console.log('📡 Parameters unchanged, skipping update');
          }
        }, 8); // Very minimal debounce since PanelGroup remounts cleanly
      }
      else if (event.data.type === 'UPDATE_SETTINGS') {
        // Handle direct settings updates for immediate changes
        if (event.data.settings) {
          
          // SAFETY: Clear orderedPillars if any React components detected
          if (event.data.settings.orderedPillars && 
              event.data.settings.orderedPillars.some(p => p && (p.$$typeof || p._owner || p.type))) {
            console.error('❌ React components detected in incoming orderedPillars - clearing for safety');
            event.data.settings.orderedPillars = [];
          }
          
          // Minimal debug only if no pillars received
          if (!event.data.settings.pillars || !Array.isArray(event.data.settings.pillars)) {
            console.warn('⚠️ No valid pillars array in settings update');
          }
        }
        
        // Immediate settings update (removed setTimeout and startTransition for real-time updates)
        // Handle branding updates from organization dashboard
        if (event.data.branding) {
          console.log('🎨 Received branding update:', event.data.branding);
          
          // CRITICAL FIX: Use urlParams state (which has orgSlug/orgIframe) instead of location.search
          // location.search may be empty after history.replaceState, but urlParams state is authoritative
          const currentParams = new URLSearchParams(urlParams);

          if (!currentParams.get('orgSlug') && orgSlugRef.current) {
            currentParams.set('orgSlug', orgSlugRef.current);
          }
          if (!currentParams.get('orgIframe') && isOrgIframeRef.current) {
            currentParams.set('orgIframe', 'true');
          }
          
          // Update only the branding-related URL parameters
          if (event.data.branding.showDisclaimer !== undefined) {
            currentParams.set('showDisclaimer', event.data.branding.showDisclaimer.toString());
          }
          if (event.data.branding.showStatusSchedule !== undefined) {
            currentParams.set('showStatusSchedule', event.data.branding.showStatusSchedule.toString());
          }
          if (event.data.branding.headerTitle !== undefined) {
            if (event.data.branding.headerTitle) {
              currentParams.set('headerTitle', event.data.branding.headerTitle);
            } else {
              currentParams.delete('headerTitle');
            }
          }
          if (event.data.branding.headerSubtitle !== undefined) {
            currentParams.set('headerSubtitle', event.data.branding.headerSubtitle);
          }
          if (event.data.branding.headerStyle !== undefined) {
            currentParams.set('headerStyle', event.data.branding.headerStyle);
          }

          // CRITICAL: Preserve existing logo params unless explicitly updated
          // Organization header reads logo from:
          // localOrganizationSettings.branding.headerLogoUrl OR urlParams.get('headerLogo')
          const existingHeaderLogo = currentParams.get('headerLogo');
          const existingHeaderLogoStyle = currentParams.get('headerLogoStyle');

          if (event.data.branding.headerLogoUrl !== undefined) {
            if (event.data.branding.headerLogoUrl) {
              currentParams.set('headerLogo', normalizeUploadUrl(event.data.branding.headerLogoUrl));
            } else {
              // If explicitly cleared, remove the param
              currentParams.delete('headerLogo');
            }
          } else if (existingHeaderLogo) {
            // Keep existing headerLogo
            currentParams.set('headerLogo', existingHeaderLogo);
          }

          if (event.data.branding.headerLogoStyle !== undefined) {
            if (event.data.branding.headerLogoStyle) {
              currentParams.set('headerLogoStyle', event.data.branding.headerLogoStyle);
            } else {
              currentParams.delete('headerLogoStyle');
            }
          } else if (existingHeaderLogoStyle) {
            currentParams.set('headerLogoStyle', existingHeaderLogoStyle);
          }
          
          // Verify critical params are preserved
          const orgSlugPreserved = currentParams.get('orgSlug');
          const orgIframePreserved = currentParams.get('orgIframe');
          console.log('🔍 [BRANDING UPDATE] URL params after branding update:', {
            orgSlug: orgSlugPreserved,
            orgIframe: orgIframePreserved,
            allParams: currentParams.toString()
          });
          
          // Update URL and trigger re-render
          const currentPath = location.pathname;
          const newUrl = `${currentPath}?${currentParams.toString()}`;
          window.history.replaceState({}, '', newUrl);
          setUrlParams(currentParams);
          
          console.log('✅ Updated branding settings in iframe (orgSlug and orgIframe preserved)');
        }

        // Update organization settings state
        if (event.data.settings) {
              console.log('✅ Applying settings update directly to iframe', {
                newPillarCount: event.data.settings.pillars?.length || 0,
                newPillars: event.data.settings.pillars || [],
                newOrderedPillarCount: event.data.settings.orderedPillars?.length || 0,
                pillarToggleInfo: event.data.settings.pillarToggle || null
              });
              
              // Enhanced: Handle pillar toggle events with better coordination
              // Skip pillar toggle handling if this is a reset operation
              if (event.data.settings.pillarToggle && !event.data.settings.resetOperation) {
                const { pillarId, pillarName, nowVisible, triggeredByUserAction } = event.data.settings.pillarToggle;
                console.log('🔄 Processing pillar toggle event:', {
                  pillarId,
                  pillarName,
                  nowVisible,
                  triggeredByUserAction,
                  currentSelectedPillarId: selectedPillarId
                });
                
                if (!nowVisible) {
                  // Pillar is being DISABLED - clear any existing suppression to allow immediate auto-selection
                  console.log('🔧 Pillar disabled - clearing auto-selection suppression for immediate fallback');
                  delete window.pillarToggleAutoSelectionSuppress;
                  
                  if (pillarId === selectedPillarId) {
                    console.log('🎯 Currently selected pillar was disabled, auto-selection will trigger after filtering');
                    // The auto-selection logic will handle this via the useEffect dependency on filteredPillars
                  }
                } else if (nowVisible && triggeredByUserAction) {
                  // Handle when a pillar is turned back ON
                  // CRITICAL: Only auto-select if this pillar was previously NOT in the filtered list
                  // This prevents overriding manual KPI clicks when user is just switching between visible pillars
                  const wasPreviouslyVisible = filteredPillars.some(p => p.id === pillarId);
                  
                  console.log('🔄 ACTIVE DATA: Pillar turned back on, deciding selection strategy:', {
                    turnedOnPillar: pillarName,
                    turnedOnPillarId: pillarId,
                    currentSelectedPillarId: selectedPillarId,
                    wasPreviouslyVisible,
                    shouldAutoSelect: !wasPreviouslyVisible
                  });
                  
                  // Only auto-select if this is a NEW pillar being enabled (not already visible)
                  if (!wasPreviouslyVisible) {
                    console.log('🎯 ACTIVE DATA: Auto-selecting newly enabled pillar:', pillarName);
                    
                    // Prevent race condition: Set a flag to temporarily disable auto-selection
                    const autoSelectionSuppressTime = Date.now() + 100; // 100ms suppression
                    
                    setSelectedPillarId(pillarId);
                    console.log('✅ ACTIVE DATA: Selection updated to newly enabled pillar');
                    
                    // Store suppression timestamp to prevent auto-selection race condition
                    window.pillarToggleAutoSelectionSuppress = autoSelectionSuppressTime;
                  } else {
                    console.log('⏭️ ACTIVE DATA: Pillar already visible, skipping auto-select to preserve manual selection');
                  }
                }
              }
              
              // Update LOCAL organization settings to prevent database overwrites
              setLocalOrganizationSettings(prevSettings => {
                const safePrevSettings = prevSettings || {};
                // Focus on key theme/header updates for debugging
                if (event.data.settings.branding) {
                  console.log('🎨 [THEME UPDATE] Received theme change:', {
                    primaryColor: event.data.settings.branding.primaryColor,
                    secondaryColor: event.data.settings.branding.secondaryColor,
                    headerStyle: event.data.settings.branding.headerStyle,
                    headerTitle: event.data.settings.branding.headerTitle,
                    fontFamily: event.data.settings.branding.fontFamily,
                    primaryFontFamily: event.data.settings.branding.primaryFontFamily,
                    secondaryFontFamily: event.data.settings.branding.secondaryFontFamily
                  });
                }

                // Prevent null/undefined branding keys from wiping out existing valid values
                const incomingBrandingRaw = event.data.settings.branding;
                const incomingBranding = incomingBrandingRaw && typeof incomingBrandingRaw === 'object'
                  ? Object.fromEntries(
                      Object.entries(incomingBrandingRaw).filter(([, v]) => v !== undefined)
                    )
                  : null;

                const normalizedIncomingBranding = incomingBranding ? normalizeBranding(incomingBranding) : null;
                
                // CRITICAL FIX: For theme-only updates, preserve existing pillars
                // Don't let branding updates overwrite pillar settings
                const isThemeOnlyUpdate = event.data.themeUpdate === true;
                
                const newSettings = {
                  ...safePrevSettings,
                  ...event.data.settings
                };
                
                // CRITICAL: Preserve pillars and orderedPillars during theme-only updates
                if (isThemeOnlyUpdate) {
                  // Keep existing pillar settings, don't overwrite with incoming (which may be empty)
                  newSettings.pillars = safePrevSettings.pillars || [];
                  newSettings.orderedPillars = safePrevSettings.orderedPillars || [];
                  console.log('🎨 [THEME ONLY] Preserving existing pillars during theme update:', {
                    preservedPillarsCount: newSettings.pillars.length,
                    preservedOrderedCount: newSettings.orderedPillars.length
                  });
                }
                
                // If branding is included in the update, merge it properly
                if (normalizedIncomingBranding) {
                  newSettings.branding = {
                    ...safePrevSettings?.branding,
                    ...normalizedIncomingBranding
                  };
                  console.log('✅ [THEME UPDATE] Applied theme changes to local settings:', {
                    primaryColor: newSettings.branding.primaryColor,
                    secondaryColor: newSettings.branding.secondaryColor,
                    headerStyle: newSettings.branding.headerStyle
                  });
                }
                return newSettings;
              });

              // Update URL param for headerTitle when theme update includes it
              // This is needed because organizationBranding prioritizes URL params over local settings
              if (event.data.settings?.branding?.headerTitle !== undefined) {
                const currentParams = new URLSearchParams(window.location.search);
                const incomingTitle = event.data.settings.branding.headerTitle;
                if (incomingTitle) {
                  currentParams.set('headerTitle', incomingTitle);
                } else {
                  currentParams.delete('headerTitle');
                }
                const currentPath = location.pathname;
                window.history.replaceState({}, '', `${currentPath}?${currentParams.toString()}`);
                setUrlParams(currentParams);
              }

              // Mark that the iframe has received authoritative settings from the parent
              if (
                (Array.isArray(event.data.settings.pillars) && event.data.settings.pillars.length > 0) ||
                (Array.isArray(event.data.settings.orderedPillars) && event.data.settings.orderedPillars.length > 0)
              ) {
                if (iframeSettingsTimeoutRef.current) {
                  clearTimeout(iframeSettingsTimeoutRef.current);
                  iframeSettingsTimeoutRef.current = null;
                  console.log('✅ [IFRAME SETTINGS] Cleared timeout - settings received');
                }
                setHasReceivedIframeSettings(true);
                try {
                  window.__orgIframeHasReceivedSettings = true;
                  window.__orgIframeContextKey = `${orgSlugRef.current || ''}:${location.pathname}`;
                } catch (_) {}
                console.log('📨 [IFRAME SETTINGS] Received UPDATE_SETTINGS with pillars - enabling org pillar filtering', {
                  pillarsCount: Array.isArray(event.data.settings.pillars) ? event.data.settings.pillars.length : 0,
                  orderedPillarsCount: Array.isArray(event.data.settings.orderedPillars) ? event.data.settings.orderedPillars.length : 0,
                  timeoutCleared: true
                });
              }
        }
        
        // Handle pillar selection messages (from reset operations)
        if (event.data.type === 'SELECT_PILLAR') {
          const { pillarId, pillarName, isResetSelection } = event.data;
          console.log('🎯 [RESET] Received pillar selection message:', {
            pillarId,
            pillarName,
            isResetSelection,
            currentSelectedPillarId: selectedPillarId
          });
          
          if (isResetSelection) {
            // For reset operations, directly set the pillar without triggering auto-selection logic
            console.log('🎯 [RESET] Setting pillar selection after reset:', pillarName);
            setSelectedPillarId(pillarId);
            setSelectedSubPillar(null); // Clear sub-pillar selection
          }
        }
        
        // Apply specific setting changes
        if (event.data.showHeader !== undefined) {
          const newUrlParams = new URLSearchParams(location.search);
          newUrlParams.set('showHeader', event.data.showHeader.toString());
          setUrlParams(newUrlParams);
          console.log('✅ Updated showHeader setting:', event.data.showHeader);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Handshake: Tell the parent org dashboard that the iframe is ready to receive UPDATE_SETTINGS.
    // Without this, the parent can postMessage too early (before our listener is installed) and the message is lost.
    try {
      const params = new URLSearchParams(location.search);
      const isOrgIframe = params.get('orgIframe') === 'true';
      const orgSlugFromUrl = params.get('orgSlug');
      if (isOrgIframe && window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'IFRAME_READY',
          orgSlug: orgSlugFromUrl,
          view: view || null,
          href: window.location.href,
          ts: Date.now()
        }, '*');
        console.log('🤝 [IFRAME] Sent IFRAME_READY to parent', { orgSlug: orgSlugFromUrl, view });
      }
    } catch (e) {
      console.warn('⚠️ [IFRAME] Failed to post IFRAME_READY:', e);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location.pathname, location.search, view]);

  // CRITICAL: Prevent AppContent from rendering for organization routes
  if (location.pathname.startsWith('/org/')) {
    return null;
  }

  // If we're on the embed page, render it without the regular app layout
  if (location.pathname === '/embed') {
    return <EmbedPage />;
  }

  useEffect(() => {
    trackPageView();
  }, [location]);

  // Read URL parameters for events panel configuration
  useEffect(() => {
    const collapsedParam = urlParams.get('eventsCollapsed');
    const widthParam = urlParams.get('eventsWidth');
    const analysisWidthParam = urlParams.get('analysisWidth');

    // Only apply URL parameters on initial load
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // Explicitly check for string values to handle both true and false cases
      const isCollapsed = collapsedParam === 'true';
      setEventsPanelCollapsed(isCollapsed);
      
      // Get current layout
      const currentLayout = largePanelGroupRef.current?.getLayout() || [25, 50, 25];
      const currentAnalysisSize = analysisWidthParam ? parseInt(analysisWidthParam, 10) : currentLayout[0];
      const currentMapSize = currentLayout[1];
      
      // Calculate new layout based on collapse state
      const mapSize = isCollapsed ? (100 - currentAnalysisSize) : (100 - currentAnalysisSize - (widthParam ? parseInt(widthParam, 10) : 25));
      const eventsSize = isCollapsed ? 0 : (widthParam ? parseInt(widthParam, 10) : 25);
      
      // Ensure all values are valid numbers before setting layout
      const newLayout = [
        Number.isFinite(currentAnalysisSize) ? currentAnalysisSize : 25,
        Number.isFinite(mapSize) ? mapSize : 50,
        Number.isFinite(eventsSize) ? eventsSize : 25
      ];
      

      
      if (largePanelGroupRef.current) {
      largePanelGroupRef.current.setLayout(newLayout);
    }
      
      // Update state for analysis panel size
      setAnalysisPanelSize(currentAnalysisSize);
    }
  }, [urlParams]); // Only run when URL parameters change

  // Organization settings state (fetched from database)
  const [organizationSettings, setOrganizationSettings] = useState(null);
  const [isOrgDashboard, setIsOrgDashboard] = useState(false);
  
  // CRITICAL: Memoize isOrgIframe separately so it doesn't change when other URL params change
  // This prevents the pillar filter from resetting on branding updates
  // We use a ref to track the initial value since orgIframe doesn't change during the session
  const isOrgIframeRef = useRef(urlParams.get('orgIframe') === 'true');
  const isOrgIframe = isOrgIframeRef.current;
  
  // Local organization settings state for live preview (prevents database overwrites)
  const [localOrganizationSettings, setLocalOrganizationSettings] = useState(null);
  
  // CRITICAL: Memoize the pillars array separately to prevent filter reset when only branding changes
  // This ensures the filterPillarsForOrganization callback only recreates when pillars actually change
  const orgPillarsArray = useMemo(() => {
    return localOrganizationSettings?.pillars || [];
  }, [localOrganizationSettings?.pillars]);
  
  const orgOrderedPillarsArray = useMemo(() => {
    return localOrganizationSettings?.orderedPillars || [];
  }, [localOrganizationSettings?.orderedPillars]);

  // IMPORTANT: For org iframes, the authoritative pillar list comes from the parent postMessage
  // We must not fall back to "all pillars" until at least one UPDATE_SETTINGS message has been applied.
  const [hasReceivedIframeSettings, setHasReceivedIframeSettings] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.__orgIframeHasReceivedSettings === true;
    } catch (_) {
      return false;
    }
  });

  // Track if we should fetch from database (prevent re-fetch during live preview)
  const [hasInitializedOrgSettings, setHasInitializedOrgSettings] = useState(false);
  
  // Extract org parameters outside useEffect for dependency tracking
  const orgSlugFromParams = urlParams.get('orgSlug');
  if (orgSlugFromParams) {
    orgSlugRef.current = orgSlugFromParams;
  }
  const orgSlug = orgSlugFromParams || orgSlugRef.current;
  const orgIframe = isOrgIframeRef.current;
  
  // Detect if this is an organization iframe and fetch settings from database
  useEffect(() => {
    if (orgSlug && orgIframe) {
      setIsOrgDashboard(true);

      const nextContextKey = `${orgSlug}:${location.pathname}`;
      const isNewContext = lastOrgIframeContextRef.current !== nextContextKey;
      if (isNewContext) {
        lastOrgIframeContextRef.current = nextContextKey;
        let keepCached = false;
        try {
          keepCached = window.__orgIframeHasReceivedSettings === true && window.__orgIframeContextKey === nextContextKey;
        } catch (_) {
          keepCached = false;
        }
        setHasReceivedIframeSettings(keepCached);
        if (!keepCached) {
          try {
            window.__orgIframeHasReceivedSettings = false;
            window.__orgIframeContextKey = nextContextKey;
          } catch (_) {}
        }
      }

      // Start (or restart) timeout only if we're still waiting for iframe settings in this context
      // AND we haven't already received them
      if (isNewContext && !hasReceivedIframeSettings) {
        if (iframeSettingsTimeoutRef.current) {
          clearTimeout(iframeSettingsTimeoutRef.current);
        }
        iframeSettingsTimeoutRef.current = setTimeout(() => {
          setHasReceivedIframeSettings((prev) => {
            if (!prev) {
              console.warn('⚠️ [IFRAME SETTINGS] Timeout (10s) waiting for parent UPDATE_SETTINGS. Falling back to DB/local defaults.');
              return true;
            }
            // Already received settings, don't change state
            return prev;
          });
        }, 10000); // Increased to 10 seconds to allow parent time to fetch pillars
      }
      
      // Only fetch from database on initial load, not on URL parameter changes during live preview
      if (!hasInitializedOrgSettings) {
        // Fetch organization settings from database
        const fetchOrganizationSettings = async () => {
          try {
            console.log('🏛️ Fetching organization settings from database (initial load):', { orgSlug });
            
            // Use current view from route params (fallback to 'default' if missing)
            const currentView = (view || 'default').toUpperCase();
            const response = await fetch(`/api/organizations/${orgSlug}/settings/${currentView}` , {
              credentials: 'include'
            });
            
            if (response.ok) {
              const settings = await response.json();
              console.log('✅ Loaded organization settings from database:', settings);
              setOrganizationSettings(settings);
              // Only apply DB settings to localOrganizationSettings if we haven't already received
              // authoritative settings from the parent via postMessage.
              let shouldApplyDbToLocal = true;
              try {
                shouldApplyDbToLocal = window.__orgIframeHasReceivedSettings !== true;
              } catch (_) {
                shouldApplyDbToLocal = true;
              }
              if (shouldApplyDbToLocal) {
                // Initialize local settings from database (only once on initial load)
                setLocalOrganizationSettings((prev) => {
                  const safePrev = prev || {};
                  const incoming = settings || {};

                  const prevPillars = Array.isArray(safePrev.pillars) ? safePrev.pillars : [];
                  const incomingPillars = Array.isArray(incoming.pillars) ? incoming.pillars : [];
                  const mergedPillars = prevPillars.length > 0 ? prevPillars : incomingPillars;

                  const prevOrdered = Array.isArray(safePrev.orderedPillars) ? safePrev.orderedPillars : [];
                  const incomingOrdered = Array.isArray(incoming.orderedPillars) ? incoming.orderedPillars : [];
                  const mergedOrdered = prevOrdered.length > 0 ? prevOrdered : incomingOrdered;

                  const prevBranding = safePrev.branding && typeof safePrev.branding === 'object' ? safePrev.branding : {};
                  const incomingBrandingRaw = incoming.branding && typeof incoming.branding === 'object' ? incoming.branding : {};
                  const incomingBranding = normalizeBranding(incomingBrandingRaw);
                  const mergedBranding = Object.keys(incomingBranding || {}).length > 0
                    ? { ...prevBranding, ...incomingBranding }
                    : prevBranding;

                  return {
                    ...safePrev,
                    ...incoming,
                    branding: mergedBranding,
                    pillars: mergedPillars,
                    orderedPillars: mergedOrdered
                  };
                });
              }
              setHasInitializedOrgSettings(true);
            } else {
              console.warn('⚠️ Failed to load organization settings, using defaults');
              const defaultSettings = { branding: {}, pillars: [] };
              setOrganizationSettings(defaultSettings);
              let shouldApplyDbToLocal = true;
              try {
                shouldApplyDbToLocal = window.__orgIframeHasReceivedSettings !== true;
              } catch (_) {
                shouldApplyDbToLocal = true;
              }
              if (shouldApplyDbToLocal) {
                setLocalOrganizationSettings((prev) => {
                  const safePrev = prev || {};
                  const prevPillars = Array.isArray(safePrev.pillars) ? safePrev.pillars : [];
                  return prevPillars.length > 0 ? safePrev : defaultSettings;
                });
              }
              setHasInitializedOrgSettings(true);
            }
          } catch (error) {
            console.error('❌ Error fetching organization settings:', error);
            const defaultSettings = { branding: {}, pillars: [] };
            setOrganizationSettings(defaultSettings);
            let shouldApplyDbToLocal = true;
            try {
              shouldApplyDbToLocal = window.__orgIframeHasReceivedSettings !== true;
            } catch (_) {
              shouldApplyDbToLocal = true;
            }
            if (shouldApplyDbToLocal) {
              setLocalOrganizationSettings((prev) => {
                const safePrev = prev || {};
                const prevPillars = Array.isArray(safePrev.pillars) ? safePrev.pillars : [];
                return prevPillars.length > 0 ? safePrev : defaultSettings;
              });
            }
            setHasInitializedOrgSettings(true);
          }
        };
        
        fetchOrganizationSettings();
      }

      return () => {
        if (iframeSettingsTimeoutRef.current) {
          clearTimeout(iframeSettingsTimeoutRef.current);
          iframeSettingsTimeoutRef.current = null;
        }
      };
    } else {
      setIsOrgDashboard(false);
      setOrganizationSettings(null);
      setLocalOrganizationSettings(null);
      setHasInitializedOrgSettings(false);
      setHasReceivedIframeSettings(false);
      try {
        window.__orgIframeHasReceivedSettings = false;
        window.__orgIframeContextKey = null;
      } catch (_) {}
      lastOrgIframeContextRef.current = null;
      if (iframeSettingsTimeoutRef.current) {
        clearTimeout(iframeSettingsTimeoutRef.current);
        iframeSettingsTimeoutRef.current = null;
      }
    }
  }, [orgSlug, orgIframe, location.pathname]); // Only re-run when actual context changes

  // Apply organization theme from local settings (includes live updates) OR URL parameters (fallback)
  useEffect(() => {
    let orgSettings = null;
    
    if (isOrgDashboard && localOrganizationSettings?.branding) {
      // Priority 1: Use local settings for organization dashboard (includes live updates)
      orgSettings = localOrganizationSettings.branding;
    } else {
      // Priority 2: Fallback to URL parameters for backward compatibility
    const primaryColor = urlParams.get('orgPrimaryColor') || urlParams.get('primaryColor');
    const secondaryColor = urlParams.get('orgSecondaryColor') || urlParams.get('secondaryColor');
    const primaryFontFamily = urlParams.get('orgPrimaryFontFamily') || urlParams.get('orgFontFamily') || urlParams.get('fontFamily');
    const secondaryFontFamily = urlParams.get('orgSecondaryFontFamily') || urlParams.get('orgFontFamily') || urlParams.get('fontFamily');
      const fontFamily = urlParams.get('orgFontFamily') || urlParams.get('fontFamily');
    const backgroundColor = urlParams.get('backgroundColor');

    if (primaryColor || secondaryColor || primaryFontFamily || secondaryFontFamily || fontFamily || backgroundColor) {
        orgSettings = {
        primaryColor: primaryColor || '#1976d2',
        secondaryColor: secondaryColor || '#dc004e',
        primaryFontFamily: primaryFontFamily || 'Roboto',
        secondaryFontFamily: secondaryFontFamily || 'Roboto',
          fontFamily: fontFamily || 'Roboto',
        backgroundColor: backgroundColor || '#f5f5f5'
      };
      }
    }

    if (orgSettings) {
      // Create a theme identifier to prevent unnecessary re-applications
      const themeId = `${orgSettings.primaryColor || 'default'}-${orgSettings.secondaryColor || 'default'}-${orgSettings.primaryFontFamily || 'default'}-${orgSettings.secondaryFontFamily || 'default'}-${orgSettings.backgroundColor || 'default'}`;
      
      // Check if this theme is already applied
      const currentThemeId = document.documentElement.getAttribute('data-org-theme-id');
      if (currentThemeId === themeId) {
        return; // Skip if same theme is already applied
      }

      if (isOrgDashboard && localOrganizationSettings?.branding) {
        console.log('🎨 Applying theme:', {
          primaryColor: orgSettings.primaryColor,
          secondaryColor: orgSettings.secondaryColor,
          headerStyle: orgSettings.headerStyle
        });
      } else {
        console.log('🎨 Applying organization theme from URL:', orgSettings);
      }

      // Create and apply the dynamic theme
      const dynamicTheme = OrganizationThemeGenerator.createDynamicTheme(orgSettings);
      changeTheme(dynamicTheme);

      // Apply CSS variables for organization branding
      OrganizationThemeGenerator.applyOrganizationCssVariables(orgSettings);

      // Increment font version to trigger map re-render with new fonts
      setFontVersion(prev => prev + 1);

      // Load the organization fonts
      if (orgSettings.primaryFontFamily) {
        loadOrganizationFont(orgSettings.primaryFontFamily);
      }
      if (orgSettings.secondaryFontFamily && orgSettings.secondaryFontFamily !== orgSettings.primaryFontFamily) {
        loadOrganizationFont(orgSettings.secondaryFontFamily);
      }
      if (orgSettings.fontFamily && orgSettings.fontFamily !== orgSettings.primaryFontFamily && orgSettings.fontFamily !== orgSettings.secondaryFontFamily) {
        loadOrganizationFont(orgSettings.fontFamily);
      }

      // Convert primary color to RGB for transparency effects
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? 
          `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : 
          '25, 118, 210';
      };
      
      document.documentElement.style.setProperty('--color-primary-rgb', hexToRgb(orgSettings.primaryColor || '#1976d2'));
      
      // Mark this theme as applied
      document.documentElement.setAttribute('data-org-theme-id', themeId);
    } else {
      // Set default RGB value for primary color transparency effects
      document.documentElement.style.setProperty('--color-primary-rgb', '25, 118, 210');
      document.documentElement.setAttribute('data-org-theme-id', 'default');
    }
  }, [urlParams, localOrganizationSettings, isOrgDashboard, changeTheme]);

  // Organization pillar filtering function
  const filterPillarsForOrganization = useCallback((pillars) => {
    // Sanitize input pillars
    if (!Array.isArray(pillars)) {
      console.error('❌ Input pillars is not an array:', typeof pillars);
      setFilteredPillars([]);
      return;
    }
    // Helper: stable visual order for pillars
    const sortByVisualOrder = (arr) => {
      return [...arr].sort((a, b) => {
        const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
        const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
        if (aCustom !== bCustom) return aCustom - bCustom;
        const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
        const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      });
    };

    // Filter out React components from the input pillars array
    const safePillars = pillars.filter((pillar, index) => {
      try {
        if (!pillar || typeof pillar !== 'object') {
          return false;
        }
        
        // Check for React component signatures
        if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
          console.error(`❌ React component detected at index ${index}:`, pillar.name || 'Unknown');
          return false;
        }
        
        return true;
      } catch (error) {
        console.error(`❌ Error validating pillar at index ${index}:`, error);
        return false;
      }
    });
    
    if (safePillars.length !== pillars.length) {
      console.warn(`⚠️ Filtered out ${pillars.length - safePillars.length} invalid pillars from input`);
    }
    
    // NOTE: Using memoized isOrgIframe from outer scope to prevent callback recreation on URL param changes
    
    if (!isOrgDashboard) {
      // Not an organization dashboard - use all safe pillars
      setFilteredPillars(safePillars);
      return;
    }
    
    // CRITICAL FIX: For org dashboards, if pillars array is empty but we have received iframe settings,
    // don't reset to all pillars - wait for proper pillar data
    if (orgPillarsArray.length === 0) {
      if (hasReceivedIframeSettings) {
        // We've received settings but pillars is empty - this shouldn't happen
        // Keep current filtered pillars instead of resetting to all
        return;
      }
      // No pillar settings yet - use all safe pillars as fallback
      setFilteredPillars(safePillars);
      return;
    }
    
    // Use memoized arrays to prevent callback recreation when only branding changes
    const orgPillarKeys = orgPillarsArray;
    let orderedPillars = orgOrderedPillarsArray;

    // Log pillar filtering only when there are issues
    if (orgPillarKeys.length === 0 && safePillars.length > 0) {
      console.log(`🔧 [Framework Filter] No organization pillars found, using all ${safePillars.length} available pillars`);
    }
    
    // EARLY SAFETY CHECK: Validate orderedPillars before any processing
    if (orderedPillars && orderedPillars.length > 0) {
      console.log('🔍 Early validation of orderedPillars:', {
        type: typeof orderedPillars,
        isArray: Array.isArray(orderedPillars),
        length: orderedPillars.length,
        hasAnyReactComponents: orderedPillars.some(p => p && (p.$$typeof || p._owner || p.type)),
        firstFewTypes: orderedPillars.slice(0, 3).map(p => typeof p),
        firstFewHaveReactProps: orderedPillars.slice(0, 3).map(p => !!(p && (p.$$typeof || p._owner || p.type)))
      });
      
      // If ANY React components detected, clear the array immediately and fallback to basic filtering
      if (orderedPillars.some(p => p && (p.$$typeof || p._owner || p.type))) {
        console.error('❌ React components detected in orderedPillars - clearing array for safety');
        orderedPillars = [];
        // Filter safe pillars based on organization settings without custom ordering
        const basicFiltered = safePillars.filter(pillar => {
          const pillarIdStr = pillar.id.toString();
          const frameworkCombo1 = pillar.framework_id ? `${pillar.id}_${pillar.framework_id}` : null;
          const frameworkCombo2 = pillar.framework_id ? `${pillar.key}_${pillar.framework_id}` : null;
          
          // Check all possible matching criteria
          const matchedByKey = orgPillarKeys.includes(pillar.key);
          const matchedById = orgPillarKeys.includes(pillarIdStr);
          const matchedByIdFramework = frameworkCombo1 && orgPillarKeys.includes(frameworkCombo1);
          const matchedByKeyFramework = frameworkCombo2 && orgPillarKeys.includes(frameworkCombo2);
          
          const isIncluded = matchedByKey || matchedById || matchedByIdFramework || matchedByKeyFramework;
          
          // DEBUG: Log framework-aware filtering (fallback mode) - ESPECIALLY for Humanitarian Access
          if (pillar.framework_id || pillar.name === 'Humanitarian Access') {
            console.log(`🔧 [Framework Filter - Fallback] "${pillar.name}":`, {
              pillarId: pillar.id,
              pillarKey: pillar.key,
              frameworkId: pillar.framework_id,
              frameworkCombo1,
              frameworkCombo2,
              matchedByKey,
              matchedById,
              matchedByIdFramework,
              matchedByKeyFramework,
              isIncluded,
              orgPillarKeys: orgPillarKeys.slice(0, 10), // Show first 10 for context
              orgPillarKeysLength: orgPillarKeys.length
            });
          }
          
          return isIncluded;
        });
        setFilteredPillars(basicFiltered);
        return;
      }
    }
    
    if (orgPillarKeys.length === 0) {
      // CRITICAL FIX: Empty array means 0 visible pillars, not "show all"
      // This respects the organization's saved settings (even if they saved 0 pillars)
      console.log('📋 [IFRAME] Organization has 0 visible pillars configured - showing empty dashboard');
      setFilteredPillars([]);
      return;
    }
    
    // Filter safe pillars based on organization settings
    // CRITICAL: Match by id_framework_id format since that's what parent sends
    let filtered = safePillars.filter(pillar => {
      const pillarId = pillar.id;
      const idFrameworkId = `${pillar.id}_${pillar.framework_id || '0'}`;
      
      // Check if pillar is in the organization's saved pillar list
      // Support: numeric ID, string ID, and id_framework_id format
      const matchedById = orgPillarKeys.includes(pillarId) || orgPillarKeys.includes(pillarId.toString());
      const matchedByIdFramework = orgPillarKeys.includes(idFrameworkId);
      
      return matchedById || matchedByIdFramework;
    });

    // DEBUG: Log final filtered results, especially for Humanitarian Access  
    console.log(`🔧 [Framework Filter Results] After filtering:`, {
      filteredPillarsCount: filtered.length,
      filteredPillarNames: filtered.map(p => p.name),
      humanitarianAccessIncluded: filtered.find(p => p.name === 'Humanitarian Access') ? true : false,
      humanitarianAccessData: filtered.find(p => p.name === 'Humanitarian Access') || 'Not found in filtered'
    });
    
    // 🎯 Apply order from organizationSettings.pillars (id_framework_id sequence)
    // Special case: For Global Workspace, always respect admin visual order
    const orgSlugFromUrl = urlParams.get('orgSlug');
    const isGlobalWorkspace = orgSlugFromUrl === 'global';

    if (!isGlobalWorkspace && isOrgDashboard && orgPillarsArray.length > 0) {
      console.log('🎯 [PILLAR ORDER] Applying order from orgPillarsArray:', {
        orgPillarKeys: orgPillarsArray,
        filteredCount: filtered.length,
        filteredPillarKeys: filtered.map(p => `${p.id}_${p.framework_id}`)
      });
      
      // Create ordered array based on orgPillarsArray sequence
      const orderedFiltered = [];
      
      // First pass: Add pillars in the order specified by orgPillarsArray
      // orgPillarsArray now contains pillar_id (numeric) values
      orgPillarsArray.forEach((orgPillarId, index) => {
        const matchingPillar = filtered.find(p => {
          // Match by pillar_id (support both numeric and string formats)
          return p.id === orgPillarId || p.id === parseInt(orgPillarId) || p.id.toString() === orgPillarId.toString();
        });
        
        if (matchingPillar) {
          // Set custom_order to match the organization settings order
          matchingPillar.custom_order = index;
          orderedFiltered.push(matchingPillar);
        }
      });
      
      // Second pass: Add any remaining pillars that weren't in orgPillarsArray
      filtered.forEach(pillar => {
        if (!orderedFiltered.includes(pillar)) {
          pillar.custom_order = orderedFiltered.length;
          orderedFiltered.push(pillar);
        }
      });
      
      filtered = orderedFiltered;
      
      console.log('✅ [PILLAR ORDER] Applied organization order successfully:', {
        orderedPillarNames: filtered.map(p => p.name),
        orderedPillarIds: filtered.map(p => p.id),
        customOrdersSet: filtered.map(p => p.custom_order),
        orderMatchesOrgSettings: filtered.slice(0, orgPillarsArray.length)
          .every((p, i) => p.id === orgPillarsArray[i] || p.id === parseInt(orgPillarsArray[i]) || p.id.toString() === orgPillarsArray[i]?.toString())
      });
    } else {
      console.log('🔍 [PILLAR ORDER] Using default order (no organization order provided):', {
        isOrgDashboard,
        hasLocalOrganizationSettings: !!localOrganizationSettings,
        hasPillarsArray: !!(localOrganizationSettings?.pillars),
        pillarsArrayLength: localOrganizationSettings?.pillars?.length || 0,
        filteredPillarNames: filtered.map(p => p.name)
      });

      // Sort by backend-provided custom_order/order_index/name without overwriting custom_order
      filtered = sortByVisualOrder(filtered);
    }
    
    setFilteredPillars(filtered);
  // CRITICAL: Use memoized orgPillarsArray and orgOrderedPillarsArray instead of localOrganizationSettings
  // This prevents callback recreation when only branding changes (which doesn't affect pillars)
  }, [isOrgDashboard, isOrgIframe, orgPillarsArray, orgOrderedPillarsArray, hasReceivedIframeSettings]);

  // View and region selection states
  const [selectedViewCode, setSelectedViewCode] = useState('');
  const [availableViews, setAvailableViews] = useState([]);
  const [selectedViewData, setSelectedViewData] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // Use our custom theme-preserving navigate hook
  const navigate = useThemeNavigate();

  // Map related states
  const [mapState, setMapState] = useState(null);
  const [mapLayers, setMapLayers] = useState([]);
  const [mapAnalysisData, setMapAnalysisData] = useState(null);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [isMapLayersReady, setIsMapLayersReady] = useState(true);
  const [mapInstance, setMapInstance] = useState(null);
  const [fontVersion, setFontVersion] = useState(0); // Track font changes for map re-rendering
  
  // Handler for receiving map analysis data from the Map component
  const handleMapAnalysisData = useCallback((data) => {
    setMapAnalysisData(data);
  }, []);
  
  // Process map analysis data when it changes
  const [parsedMapContent, setParsedMapContent] = useState(null);
  const [mapUpdatedAt, setMapUpdatedAt] = useState(null);
  
  useEffect(() => {
    if (mapAnalysisData) {
      // Set the updated_at date
      if (mapAnalysisData.updated_at) {
        setMapUpdatedAt(new Date(mapAnalysisData.updated_at));
      }
      
      // Try to parse the content if it's a JSON string
      if (mapAnalysisData.content && typeof mapAnalysisData.content === 'string') {
        try {
          const parsedContent = JSON.parse(mapAnalysisData.content);
          
          // Check if data and locations arrays are empty
          const hasEmptyData = !parsedContent.data || parsedContent.data.length === 0;
          const hasEmptyLocations = !parsedContent.locations || parsedContent.locations.length === 0;
          
          // Only set parsed content if there's actual data to display
          if (!hasEmptyData || !hasEmptyLocations) {
            setParsedMapContent(parsedContent);
          } else {
            setParsedMapContent(null);
          }
        } catch (error) {
          setParsedMapContent(null);
        }
      } else {
        setParsedMapContent(null);
      }
    } else {
      setParsedMapContent(null);
      setMapUpdatedAt(null);
    }
  }, [mapAnalysisData]);
  const [showLayersOnMap, setShowLayersOnMap] = useState(true); // New state for layer visibility

  // Map ref for methods like flyTo
  const mapRef = useRef();

  // Event and filtering states
  const [selectedPillarId, setSelectedPillarId] = useState(null); // always parent pillar
  const [selectedSubPillar, setSelectedSubPillar] = useState(null);
  const [parentPillar, setParentPillar] = useState(null);
  const [subPillars, setSubPillars] = useState([]);
  const [allViewPillars, setAllViewPillars] = useState([]); // New state for all pillars for the view
  const [filteredPillars, setFilteredPillars] = useState([]); // Organization-filtered pillars
  const [isLoadingAllPillars, setIsLoadingAllPillars] = useState(false); // Loading state for all pillars fetch
  const [allPillarsError, setAllPillarsError] = useState(null); // Error state for all pillars fetch
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedActorType, setSelectedActorType] = useState(null);
  const [selectedEventCategories, setSelectedEventCategories] = useState([]);
  const [filteredEventList, setFilteredEventList] = useState([]);
  const [previousEventsPillarId, setPreviousEventsPillarId] = useState(null);
  const [isInitialPillarSet, setIsInitialPillarSet] = useState(false); // New state for smoother initial loading



  // Time range state
  const [selectedTimeRange, setSelectedTimeRange] = useState([
    new Date(Date.UTC(2024, 6, 1)).getTime(),  // July 1st (month is 0-based)
    new Date(Date.UTC(2024, 11, 31)).getTime()  // December 31st
  ]);

  // Apply organization filtering when settings change
  useEffect(() => {
    if (allViewPillars.length > 0) {
      filterPillarsForOrganization(allViewPillars);
    }
  }, [allViewPillars, filterPillarsForOrganization]);

  // Fetch max ACLED date and set time range
  useEffect(() => {
    const fetchMaxDate = async () => {
      try {
        const response = await fetch(`${API_URL}/api/acled/max-date`);
        if (!response.ok) {
          throw new Error('Failed to fetch max ACLED date');
        }
        const { maxDate } = await response.json();
        if (maxDate) {
          const endDate = new Date(maxDate);
          // Set to first day of month, 3 months ago
          const startDate = new Date(endDate);
          startDate.setMonth(startDate.getMonth() - 2);
          startDate.setDate(1); // First day of month
          startDate.setHours(0, 0, 0, 0); // Start of day
          
          // Set end date to end of its day
          endDate.setHours(23, 59, 59, 999);
          
          setSelectedTimeRange([startDate.getTime(), endDate.getTime()]);
        }
      } catch (error) {
        console.error('Error fetching max ACLED date:', error);
      }
    };
    fetchMaxDate();
  }, []); // Run once on component mount

  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventListError, setEventListError] = useState(null);
  const [isTimeChartVisible, setIsTimeChartVisible] = useState(false);
  const [chartAnimationDuration, setChartAnimationDuration] = useState(750);
  const [legendTopPadding, setLegendTopPadding] = useState(1);
  const [interactionChartData, setInteractionChartData] = useState([]);
  const [isCol3Collapsed, setIsCol3Collapsed] = useState(true);
  const eventListScrollRef = useRef();
  
  // Add loading state for pillar transitions
  const [isPillarTransitioning, setIsPillarTransitioning] = useState(false);
  const [pendingEventListUpdate, setPendingEventListUpdate] = useState(null);

  // Screen size breakpoints
  const SCREEN_SIZES = {
    SMALL: 'small',   // < 768px
    MEDIUM: 'medium', // 768px - 1200px
    LARGE: 'large'    // > 1200px
  };

  const getScreenSize = () => {
    const width = window.innerWidth;
    if (width < 768) return SCREEN_SIZES.SMALL;
    if (width < 1200) return SCREEN_SIZES.MEDIUM;
    return SCREEN_SIZES.LARGE;
  };

  const [screenSize, setScreenSize] = useState(getScreenSize());

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setScreenSize(getScreenSize());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add state for panel sizes
  const [analysisPanelSize, setAnalysisPanelSize] = useState(25);
  const [mapPanelSize, setMapPanelSize] = useState(50);
  const [eventsPanelSize, setEventsPanelSize] = useState(25);
  const [eventsPanelCollapsed, setEventsPanelCollapsed] = useState(false);

  // Add state for tracking active resize and safe mounting
  const [isResizing, setIsResizing] = useState(false);
  const [isLayoutInitialized, setIsLayoutInitialized] = useState(false);
  const [isSafeMounted, setIsSafeMounted] = useState(false);
  const [isStateStabilized, setIsStateStabilized] = useState(false);
  const initialSelectionPerformedRef = useRef(false);

  // Ref for the large screen PanelGroup
  const largePanelGroupRef = useRef(null);

  // SAFE MOUNT: Set isSafeMounted to true after a small delay to ensure React has fully mounted the component
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSafeMounted(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Load initial panel layout state from cookie or localStorage
  useEffect(() => {
    const collapsedParam = urlParams.get('eventsCollapsed');
    const widthParam = urlParams.get('eventsWidth');
    const analysisWidthParam = urlParams.get('analysisWidth');
    
    // Start with default values
    let loadedLayout = [25, 50, 25];
    let isCollapsed = false;

    // If URL parameters exist, they take precedence
    if (collapsedParam !== null || widthParam !== null || analysisWidthParam !== null) {
      isCollapsed = collapsedParam === 'true';
      const defaultEventSize = 23;
      const analysisSize = analysisWidthParam ? parseInt(analysisWidthParam, 10) : 25;
      loadedLayout = [
        analysisSize, // analysis
        isCollapsed ? (100 - analysisSize) : (100 - analysisSize - (widthParam ? parseInt(widthParam, 10) : defaultEventSize)), // map
        isCollapsed ? 0 : (widthParam ? parseInt(widthParam, 10) : defaultEventSize) // events
      ];
    } else {
      // Otherwise load from cookie/localStorage
      const cookieLayout = getCookie('dashboard-panel-layout');
      const localStorageLayout = localStorage.getItem('dashboard-panel-layout');
      const savedLayoutData = cookieLayout || (localStorageLayout ? JSON.parse(localStorageLayout) : null);

      if (savedLayoutData) {
        try {
          if (savedLayoutData && typeof savedLayoutData.analysis === 'number' && 
              typeof savedLayoutData.map === 'number' && 
              typeof savedLayoutData.events === 'number') {
            setAnalysisPanelSize(savedLayoutData.analysis);
            setMapPanelSize(savedLayoutData.map);
            setEventsPanelSize(savedLayoutData.events);
            isCollapsed = savedLayoutData.eventsCollapsed || false;
            loadedLayout = [
              savedLayoutData.analysis,
              savedLayoutData.eventsCollapsed ? (100 - savedLayoutData.analysis) : savedLayoutData.map,
              savedLayoutData.eventsCollapsed ? 0 : savedLayoutData.events
            ];
          }
        } catch (error) {
          console.error('[Layout] Error loading saved layout, using defaults:', error);
        }
      }
    }

    // Ensure all values are valid numbers
    loadedLayout = loadedLayout.map(size => Number.isFinite(size) ? size : 25);
    
    // Set collapsed state
    setEventsPanelCollapsed(isCollapsed);

    // Set layout imperatively IF on large screen and ref is available
    const timer = setTimeout(() => {
      if (largePanelGroupRef.current) {
        largePanelGroupRef.current.setLayout(loadedLayout);
      }
      setIsLayoutInitialized(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array - run only once on mount

  // Save panel layout to both cookie and localStorage when it changes
  const saveLayout = useCallback((panels) => {
    // Only save after initial layout is loaded
    if (!isLayoutInitialized) {
      return; 
    }

    // Check if we have URL parameters that should override the save
    const hasUrlParams = urlParams.has('eventsCollapsed') || urlParams.has('eventsWidth') || urlParams.has('analysisWidth');
    
    // Don't save if we're using URL parameters
    if (hasUrlParams) {
      return;
    }
    
    // Ensure all values are valid numbers
    const validatedPanels = panels.map(size => 
      Number.isFinite(size) ? size : 25
    );
    
    // Use the layout directly provided by the onLayout event
    const layoutData = {
      analysis: validatedPanels[0],
      map: validatedPanels[1],
      events: validatedPanels[2],
      // Use the current state for collapsed status, as onLayout doesn't report this
      eventsCollapsed: eventsPanelCollapsed 
    };

    
    // Save to localStorage
    localStorage.setItem('dashboard-panel-layout', JSON.stringify(layoutData));
    
    // Save to cookie
    setCookie('dashboard-panel-layout', layoutData);
    
  }, [eventsPanelCollapsed, isLayoutInitialized, urlParams]);

  const appRenderCount = useRef(0);
  useEffect(() => {
    appRenderCount.current += 1;
  });
  
  // Use the map data hook
  const {
    mapSettings,
    mapData,
    isLoading: isMapDataLoading,
    isViewDataLoaded: isMapViewDataLoaded,
    error: mapDataError
  } = useMapData(selectedViewCode);
  
  // Extract view-specific adm1Data from mapData for use in components
  const adm1Data = mapData?.adm1Data?.[selectedViewCode] || [];

  // Memoize view object to prevent unnecessary Map re-renders
  const viewForMap = useMemo(() => 
    selectedViewCode ? { iso3: selectedViewCode } : null,
    [selectedViewCode]
  );

  // Determine which pillar ID should be used for event-level API calls.
  // If a sub-pillar is currently selected (e.g. when its parent pillar is hidden),
  // we should fetch events for that sub-pillar; otherwise fall back to the parent pillar.
  const eventsPillarId = selectedPillarId; // Always fetch events for main pillar, not sub-pillars

  // Use the event data hook with the effective pillar id
  const {
    activeData,
    eventListData,
    totalAcledData,
    isLoading: isEventDataLoading,
    error: eventDataError,
    refreshEventData
  } = useEventData(selectedViewData, eventsPillarId);

  // Derived loading state for event list: initial setup, data fetching, or pillar transitioning
  const eventListLoading = !isInitialPillarSet || isEventDataLoading || isPillarTransitioning;
  


  // Create live organization settings from filteredPillars for preview functionality
  const liveOrganizationSettings = useMemo(() => {
  if (!isOrgDashboard && !forceView) {
      return localOrganizationSettings;
    }
    
    // Priority 1: Use localOrganizationSettings.pillars if it's more recent than filteredPillars
    // This handles real-time updates where localOrganizationSettings is updated before filteredPillars
    if (localOrganizationSettings?.pillars && Array.isArray(localOrganizationSettings.pillars) && localOrganizationSettings.pillars.length > 0) {
      return localOrganizationSettings; // Use as-is since pillars are already in the correct format
    }
    
    // Priority 2: Fall back to filteredPillars if localOrganizationSettings.pillars is not available
    if (filteredPillars && filteredPillars.length > 0) {
      const livePillarKeys = filteredPillars.map(p => `${p.id}_${p.framework_id}`);
      
      return {
        ...localOrganizationSettings,
        pillars: livePillarKeys
      };
    }
    
    return localOrganizationSettings;
  }, [isOrgDashboard, filteredPillars, localOrganizationSettings]);

  // Use the KPI data hook with LIVE organization settings for filtering
  const {
    kpiData,
    isLoading: isKPIDataLoading,
    error: kpiDataError,
    refreshKPIData
  } = useKPIData(selectedViewData, liveOrganizationSettings);

  // Sort kpiData based on filteredPillars custom order for drag functionality
  const orderedKpiData = useMemo(() => {
    if (!kpiData || kpiData.length === 0 || !filteredPillars || filteredPillars.length === 0) {
      return kpiData;
    }
    
    // Create a map of pillar ID to custom_order for quick lookup
    const orderMap = new Map();
    filteredPillars.forEach((pillar, index) => {
      orderMap.set(pillar.id, pillar.custom_order !== undefined ? pillar.custom_order : index);
    });
    
    // Sort kpiData based on the order from filteredPillars
    const sorted = [...kpiData].sort((a, b) => {
      const orderA = orderMap.get(a.pillarId) ?? 999;
      const orderB = orderMap.get(b.pillarId) ?? 999;
      return orderA - orderB;
    });
    
    return sorted;
  }, [kpiData, filteredPillars]);

  // Use the ACLED data hook
  const {
    acledData,
    isLoading: isACLEDLoading,
    error: acledError,
    refreshData: refreshACLEDData // Destructure and alias refreshData
  } = useACLEDData(selectedViewData);

  // ANALYSIS DATA SOURCE SELECTION:
  // This determines which pillar's data to fetch for the Analysis component
  // Priority: Sub-pillar (if selected) > Parent pillar
  // This follows the same logic as handleBoxClick - if we have a sub-pillar selected,
  // it means the parent was hidden and we're showing sub-pillar-specific analysis
  const derivedAnalysisSubPillar = useMemo(() => {
    if (selectedSubPillar?.id) {
      return selectedSubPillar;
    }

    if (!selectedPillarId || filteredPillars.length === 0) {
      return null;
    }

    const selectedParentPillar = filteredPillars.find((pillar) => pillar.id === selectedPillarId);
    if (!selectedParentPillar || selectedParentPillar.is_visible !== false) {
      return null;
    }

    const firstVisibleChild = filteredPillars
      .filter((pillar) => pillar.parent_pillar_id === selectedParentPillar.id && pillar.is_visible === true)
      .sort((a, b) => {
        const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
        const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
        if (aCustom !== bCustom) return aCustom - bCustom;
        const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
        const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      })[0] || null;

    return firstVisibleChild;
  }, [filteredPillars, selectedPillarId, selectedSubPillar]);

  const effectiveAnalysisSubPillar = selectedSubPillar || derivedAnalysisSubPillar;
  const analysisPillarId = effectiveAnalysisSubPillar?.id || selectedPillarId;
  const { 
    data: analysisDataFromHook, // Renamed
    isLoading: isLoadingFromHook, // Renamed
    error: errorFromHook,       // Renamed
    refreshData: refreshAnalysisData,
    loadAnalysisById // Get the load function
  } = useAnalysisData(
    isInitialPillarSet ? selectedViewData?.id : null,
    isInitialPillarSet ? analysisPillarId : null
  );
  
  // Ensure selectedPillar is defined before use (match against the pillar id used for fetching events)
  const selectedPillar = activeData.find(p => p.id === eventsPillarId);
  
  // Determine final loading, data, and error states for AnalysisComponent
  const finalIsAnalysisLoading = !isInitialPillarSet || isLoadingFromHook;
  
  // Only provide data/error if the hook isn't loading for the current pillar AND initial setup is done
  const finalAnalysisData = isInitialPillarSet && !isLoadingFromHook && analysisDataFromHook ? analysisDataFromHook : null;
  const finalAnalysisError = isInitialPillarSet && !isLoadingFromHook ? errorFromHook : null;

  const prevAnalysisIdRef = useRef(null);
  const prevIsLoadingRef = useRef(finalIsAnalysisLoading);

  const analysisProps = useMemo(() => {
    // Analysis props configured
    
    return {
    data: finalAnalysisData,
    isLoading: finalIsAnalysisLoading,
    error: finalAnalysisError,
    viewName: selectedViewData?.name,
    pillarName: parentPillar?.name,
    subPillarName: effectiveAnalysisSubPillar?.name,
    subPillarId: effectiveAnalysisSubPillar?.id,
    onNavigateToAnalysis: loadAnalysisById // Pass the navigation callback
    };
  }, [finalAnalysisData, finalIsAnalysisLoading, finalAnalysisError, selectedViewData, parentPillar, effectiveAnalysisSubPillar, selectedPillarId, analysisPillarId, loadAnalysisById]);

  const analysisPanel = useMemo(() => {
    // 🚨 [DEBUG] CRITICAL: Inspect analysisProps for React component contamination before AnalysisComponent render
    const reactContaminationCheck = (obj, path = '') => {
      if (!obj || typeof obj !== 'object') return;
      
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        
        if (value && typeof value === 'object') {
          // Check for React component signatures
          if (value.$$typeof) {
            console.error(`🚨 [ANALYSIS_CONTAMINATION] Found React component at ${currentPath}:`, {
              $$typeof: value.$$typeof?.toString(),
              type: value.type?.name || 'unknown',
              value: value
            });
          }
          
          // Check for MapComponent specifically
          if (value.type?.name === 'MapComponent' || 
              (value.render && value.render.toString().includes('MapComponent'))) {
            console.error(`🚨 [ANALYSIS_CONTAMINATION] Found MapComponent at ${currentPath}:`, value);
          }
          
          // Recursively check nested objects
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              if (item && typeof item === 'object') {
                reactContaminationCheck(item, `${currentPath}[${index}]`);
              }
            });
          } else {
            reactContaminationCheck(value, currentPath);
          }
        }
      }
    };
    
    // Safety check for React contamination
    reactContaminationCheck(analysisProps, 'analysisProps');
    
    return (
      <AnalysisComponent
        key={`analysis-${analysisProps.data?.id || 'none'}-${analysisProps.isLoading ? 'loading' : 'ready'}`}
        {...analysisProps}
      />
    );
  }, [analysisProps]);
  
  // New: Memoized list of currently VISIBLE sub-pillars for the active parentPillar
  const visibleSubPillars = useMemo(() => {
    if (!parentPillar || !subPillars || subPillars.length === 0) {
      return [];
    }
    return subPillars.filter(sp => sp.is_visible === true);
  }, [parentPillar, subPillars]);
  
  const renderPanels = () => {
    
    const panelContent = {
      analysis: analysisPanel,
      map: (
        <div className={`map-panel ${screenSize}`}>
          <div className="map-content">
            <div className="location-dropdown-container">
              <LocationDropdown
                selectedViewData={selectedViewData}
                onLocationSelect={handleLocationSelect}
              />
            </div>
            <div id="map_panel" key="map-panel-container">
              {/* The conditional rendering here was causing the map to remount.
                  The loading logic is now handled inside OpenLayersMap. */}
              <MapComponent
                ref={mapRef}
                setMap={setMapInstance}
                view={viewForMap}
                mapOptions={memoizedMapConfiguration}
                layers={mapLayers}
                mapSettings={mapSettings}
                selectedPillarKey={activePillarKeyForDTM}
                selectedSubPillar={effectiveAnalysisSubPillar}
                onMapAnalysisData={handleMapAnalysisData}
                onUpdateLayers={setMapLayers}
                eventsPanelCollapsed={eventsPanelCollapsed}
                selectedPillarId={selectedPillarId}
                showHeader={showHeader}
                selectedView={selectedViewData}
                adm1Data={adm1Data}
                fontVersion={fontVersion}
              />
              
              {/* Subtle scroll indicator when charts are available */}
              {(activePillarKeyForDTM === 'conflict' || activePillarKeyForDTM?.startsWith('conflict_')) && (
                <div className="scroll-indicator">
                  <span>Scroll for charts</span>
                  <div className="scroll-arrow">↓</div>
                </div>
              )}
            </div>

            {/* Charts below the fold - scroll down to see them */}
            {(activePillarKeyForDTM === 'conflict' || activePillarKeyForDTM?.startsWith('conflict_')) && (
              <div className="below-fold-content">
                <div className="events-list-stats">
                  <h3>ACLED Political Violence Events</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="events-count">
                      <span className="label">Events</span>
                      <b className="count">{formatEventCount(totalEvents)}</b>
                    </span>
                    <div className="divider"></div>
                    <span className="fatalities-count">
                      <span className="label">Fatalities</span>
                      <b className="count">{formatFatalityCount(totalFatalities)}</b>
                    </span>
                  </div>
                </div>

                <TimeChart 
                  data={timechartData}
                  timeRange={selectedTimeRange}
                  onTimeRangeChange={handleTimeRangeChange}
                  selectedTypes={selectedEventTypes}
                  onEventTypeToggle={handleEventTypeToggle}
                  onEventTypeReset={handleResetEventTypes}
                />
                <InteractionChart 
                  data={interactionChartData}
                  selectedTypes={selectedEventTypes}
                  onActorSelect={handleActorSelect}
                  selectedActor={selectedActorType}  
                  selectedCategories={selectedEventCategories} 
                  onCategoryToggle={handleCategoryToggle}   
                />
              </div>
            )}
            {activePillarKeyForDTM === 'internally_displaced_persons' && (
              <div className="below-fold-content">
                <DtmTrendsChart 
                  view={selectedViewData?.iso3} 
                  selectedLocation={selectedRegion}
                  showOnlyBadge={false}
                />
              </div>
            )}
          </div>
        </div>
      ),
      eventsList: (
        <div className={`events-list-panel ${screenSize}`}>
          <div className="events-list-content">
            <div className="events-list-header">
              <h2 className="events-list-title">Recent Events</h2>
              <Button
                variant="text"
                size="small"
                // Use imperative API for large screens, fallback for others
                onClick={() => {
                  const panelGroup = largePanelGroupRef.current;
                  if (panelGroup && screenSize === SCREEN_SIZES.LARGE) {
                    const defaultEventSize = 23;
                    const minMapSize = 30; 
                    
                    // Get the CURRENT layout directly from the panel group
                    const currentLayout = panelGroup.getLayout(); 
                    const currentAnalysisSize = currentLayout[0]; // Use actual current size
                    const currentMapSize = currentLayout[1]; // We might need this


                    if (eventsPanelCollapsed) {
                      // --- Expanding --- 
                      // Calculate new sizes, preserving current analysis size
                      let newMapSize = 100 - currentAnalysisSize - defaultEventSize;
                      let finalEventSize = defaultEventSize;
                      if (newMapSize < minMapSize) {
                        newMapSize = minMapSize;
                        finalEventSize = 100 - currentAnalysisSize - newMapSize;
                      }
                      const newLayout = [
                        Number.isFinite(currentAnalysisSize) ? currentAnalysisSize : 25,
                        Number.isFinite(newMapSize) ? newMapSize : 50,
                        Number.isFinite(finalEventSize) ? finalEventSize : 25
                      ];
                      
                      panelGroup.setLayout(newLayout); 
                      setEventsPanelCollapsed(false);
                      // Only update states for panels that changed size
                      setMapPanelSize(newMapSize); 
                      setEventsPanelSize(finalEventSize); 
                      saveLayout(newLayout);
                      
                    } else {
                      // --- Collapsing ---
                      // Calculate new sizes, preserving current analysis size
                      // Give the collapsed space to the map panel
                      const newMapSize = currentMapSize + eventsPanelSize; // Add event size to map size
                      const newLayout = [
                        Number.isFinite(currentAnalysisSize) ? currentAnalysisSize : 25,
                        Number.isFinite(newMapSize) ? newMapSize : 50,
                        0 // Collapse event panel
                      ];
                      
                      panelGroup.setLayout(newLayout); 
                      setEventsPanelCollapsed(true);
                      // Only update states for panels that changed size
                      setMapPanelSize(newMapSize);
                      setEventsPanelSize(0);
                      saveLayout(newLayout);
                    }
                  } else {
                     // Fallback for smaller screens or if ref not ready
                     setEventsPanelCollapsed(!eventsPanelCollapsed);
                  }
                }}
                sx={{ 
                  marginLeft: 'auto', 
                  minWidth: 'auto', 
                  px: 1,
                  color: 'var(--color-primary)',
                  '&:hover': {
                    backgroundColor: 'var(--color-primary-08)'
                  }
                }}
              >
                {eventsPanelCollapsed ? 'Expand' : 'Collapse'}
              </Button>
            </div>
            {eventListLoading ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                flex={1}
                height="100%"
                minHeight="300px"
                sx={{ 
                  position: 'relative',
                  width: '100%'
                }}
              >
                <CircularProgress sx={{ color: 'var(--color-primary)' }} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Loading events...
                </Typography>
              </Box>
            ) : eventListError ? (
              <div className="error">Error: {eventListError}</div>
            ) : (
              <>
                <EventListComponent 
                  events={filteredEventList} 
                  ref={eventListScrollRef}
                  loading={eventListLoading}
                />
              </>
            )}
          </div>
        </div>
      )
    };

    // SAFETY GUARD: Don't render PanelGroup components until safe mounting is complete
    if (!isSafeMounted) {
      return (
        <div className="body-container loading-layout" style={{ 
          height: showHeader ? 'calc(100vh - 134px)' : 'calc(100vh - 70px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-secondary, #666)',
          fontSize: '14px'
        }}>
          <div>Initializing dashboard...</div>
        </div>
      );
    }

    // Define responsive layouts using the PanelGroup component
    if (screenSize === SCREEN_SIZES.SMALL) {
      // For small screens, use a simple, non-resizable, single-column layout
      return (
        <div className={`body-container mobile-view ${isPillarTransitioning ? 'pillar-transitioning' : ''}`}>
          {/* Map Panel - First (moved above Analysis) */}
          <div className="map-panel mobile-map-panel">
            {panelContent.map}
          </div>

          {/* Analysis Panel - Second */}
          <div className="analysis-panel mobile-analysis-panel">
             <div className={visibleSubPillars.length > 0 ? 'has-subpillars' : ''}>
              {visibleSubPillars.length > 0 && (
                  <div className="subpillar-buttons-container">
                    <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 1,
                      mt: 1,
                      ml: 0.5,
                      alignItems: 'center',
                      borderRadius: 0.15,
                      p: 0.5,
                      width: 'calc(100% - 14px)',
                      overflowX: 'auto',
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': {
                        display: 'none'
                      }
                    }}
                  >
                    {parentPillar && parentPillar.is_visible === true && (
                      <IconButton
                        onClick={() => {
                          setSelectedSubPillar(null);
                          if (parentPillar) setSelectedPillarId(parentPillar.id);
                        }}
                        size="small"
                        sx={{
                          height: 27,
                          width: 24,
                          bgcolor: !selectedSubPillar ? 'var(--color-primary)' : 'transparent',
                          color: !selectedSubPillar ? 'white' : 'text.secondary',
                          borderRadius: 0.55,
                          padding: 0,
                          '&:hover': {
                            bgcolor: !selectedSubPillar ? 'primary.dark' : 'grey.200'
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 'var(--font-size-md)'
                          }
                        }}
                        title="Show parent pillar"
                      >
                        <HomeIcon />
                      </IconButton>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {visibleSubPillars.map((subPillarItem) => {
                        const isSelected = selectedSubPillar?.id === subPillarItem.id;
                        return (
                          <Button
                            key={subPillarItem.id}
                            onClick={() => {
                              if (filteredEventList.length === 0) {
                                setIsPillarTransitioning(true);
                              }
                              setSelectedSubPillar(subPillarItem);
                            }}
                            size="small"
                            disableElevation
                            sx={{
                              height: 27,
                              py: 0,
                              px: 1,
                              pt: '1px',
                              minWidth: 'auto',
                              fontSize: 'var(--font-size-md)',
                              lineHeight: 1,
                              textTransform: 'none',
                              borderRadius: '6px 6px 0px 0px',
                              color: isSelected ? 'white' : 'text.secondary',
                              bgcolor: isSelected ? 'var(--color-primary)' : 'transparent',
                              border: 'none',
                              fontWeight: 600,
                              fontFamily: 'var(--font-family-heading) !important',
                              borderRight: '1px solid rgba(0, 0, 0, 0.06)',
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.dark' : 'grey.200',
                                border: 'none',
                              }
                            }}
                          >
                            {subPillarItem.name}
                          </Button>
                        );
                      })}
                    </Box>
                    </Box>
                  </div>
              )}
              {panelContent.analysis}
            </div>
          </div>

          {/* Events List Panel - Third */}
          <div className="events-list-panel mobile-events-panel">
            {panelContent.eventsList}
          </div>
        </div>
      );
    } else if (screenSize === SCREEN_SIZES.MEDIUM) {
      // For medium screens, render map and recent events as a single scrollable section (no divider)
      return (
        <div className={`body-container ${screenSize}screen ${isPillarTransitioning ? 'pillar-transitioning' : ''}`} style={{ 
           height: showHeader ? 'calc(100vh - 134px)' : 'calc(100vh - 70px)'
        }}>
          <PanelErrorBoundary>
            <PanelGroup 
              direction="horizontal" 
              onLayout={saveLayout} 
              id="medium-layout"
              key={`medium-${showHeader ? 'with-header' : 'no-header'}`}
              className={`${isResizing ? 'resizing' : ''} ${isPillarTransitioning ? 'pillar-transitioning' : ''}`}
            >
            <Panel id="analysis-panel" defaultSize={analysisPanelSize} minSize={20}>
              <div className={visibleSubPillars.length > 0 ? 'has-subpillars' : ''}> {/* Use visibleSubPillars.length */}
                {visibleSubPillars.length > 0 && ( // Use visibleSubPillars.length
                  <div className="subpillar-buttons-container">
                  <Box sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 1,
                      mt: 1,
                      ml: 0.5,
                      alignItems: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1.5,
                      p: 0.5,
                      width: '100%',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      overflowX: 'auto',
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': {
                        display: 'none'
                      }
                    }}
                  >
                    {/* Log parentPillar visibility for Home icon decision */}
                    {/* Show Home button whenever parent pillar is visible */}
                    {parentPillar && parentPillar.is_visible === true && (
                      <IconButton
                        onClick={() => {
                          setIsPillarTransitioning(true);
                          setSelectedSubPillar(null);
                          if (parentPillar) setSelectedPillarId(parentPillar.id);
                        }}
                        size="small"
                        sx={{
                          height: 24,
                          width: 24,
                          bgcolor: !selectedSubPillar ? 'var(--color-primary)' : 'transparent',
                          color: !selectedSubPillar ? 'white' : 'text.secondary',
                          borderRadius: 0.75,
                          padding: 0,
                          '&:hover': {
                            bgcolor: !selectedSubPillar ? 'primary.dark' : 'grey.200'
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 'var(--font-size-md)'
                          }
                        }}
                        title="Show parent pillar"
                      >
                        <HomeIcon />
                      </IconButton>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {visibleSubPillars.map((subPillarItem) => { // Map over visibleSubPillars
                        const isSelected = selectedSubPillar?.id === subPillarItem.id;
                        return (
                          <Button
                            key={subPillarItem.id}
                            onClick={() => {
                              if (filteredEventList.length === 0) {
                                setIsPillarTransitioning(true);
                              }
                              setSelectedSubPillar(subPillarItem);
                            }}
                            size="small"
                            disableElevation
                            sx={{
                              height: 25,
                              py: 0,
                              px: 1,
                              pt: '1px',
                              minWidth: 'auto',
                              fontSize: 'var(--font-size-md)',
                              lineHeight: 1,
                              textTransform: 'none',
                              borderRadius: '6px 6px 0px 0px',
                              color: isSelected ? 'white' : 'text.secondary',
                              bgcolor: isSelected ? 'var(--color-primary)' : 'transparent',
                              border: 'none',
                              fontWeight: 600,
                              fontFamily: 'var(--font-family-heading) !important',
                              borderRight: '1px solid rgba(0, 0, 0, 0.06)',
                              // opacity: subPillarItem.is_visible ? 1 : 0.5, // No longer needed
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.dark' : 'grey.200',
                                border: 'none',
                                // cursor: subPillarItem.is_visible ? 'pointer' : 'default', // No longer needed
                              }
                            }}
                          >
                            {subPillarItem.name}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                  </div>
                )}
                {panelContent.analysis}
              </div>
            </Panel>
            <ResizeHandle onDraggingChange={setIsResizing} />
            <Panel id="map-events-panel" defaultSize={100 - analysisPanelSize}>
              <div className="map-and-events-medium" style={{display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto'}}>
                {panelContent.map}
                {panelContent.eventsList}
              </div>
            </Panel>
            </PanelGroup>
          </PanelErrorBoundary>
        </div>
      );
    } else {
      // For large screens, three-column layout
      
      return (
        <div className={`body-container ${screenSize}screen largescreen ${isPillarTransitioning ? 'pillar-transitioning' : ''}`} style={{ 
            height: showHeader ? 'calc(100vh - 211px)' : 'calc(100vh - 147px)'
        }}>
          <PanelErrorBoundary>
            <PanelGroup 
              direction="horizontal" 
              onLayout={saveLayout} 
              id="large-layout"
              key={`large-${showHeader ? 'with-header' : 'no-header'}`}
              className={`${isResizing ? 'resizing' : ''} ${isPillarTransitioning ? 'pillar-transitioning' : ''}`}
              ref={largePanelGroupRef}
            >
            <Panel id="analysis-panel" defaultSize={analysisPanelSize} minSize={15}>
              <div className={visibleSubPillars.length > 0 ? 'has-subpillars' : ''}> {/* Use visibleSubPillars.length */}
                {visibleSubPillars.length > 0 && ( // Use visibleSubPillars.length
                  <Box sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 1,
                      mt: 1,
                      ml: 0.5,
                      alignItems: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 1.5,
                      p: 0.5,
                      width: '100%',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      overflowX: 'auto',
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': {
                        display: 'none'
                      }
                    }}
                  >
                    {/* Log parentPillar visibility for Home icon decision */}
                    {/* Show Home button whenever parent pillar is visible */}
                    {parentPillar && parentPillar.is_visible === true && (
                      <IconButton
                        onClick={() => {
                          setIsPillarTransitioning(true);
                          setSelectedSubPillar(null);
                          if (parentPillar) setSelectedPillarId(parentPillar.id);
                        }}
                        size="small"
                        sx={{
                          height: 24,
                          width: 24,
                          bgcolor: !selectedSubPillar ? 'var(--color-primary)' : 'transparent',
                          color: !selectedSubPillar ? 'white' : 'text.secondary',
                          borderRadius: 0.75,
                          padding: 0,
                          '&:hover': {
                            bgcolor: !selectedSubPillar ? 'primary.dark' : 'grey.200'
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 'var(--font-size-md)'
                          }
                        }}
                        title="Show parent pillar"
                      >
                        <HomeIcon />
                      </IconButton>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {visibleSubPillars.map((subPillarItem) => { // Map over visibleSubPillars
                        const isSelected = selectedSubPillar?.id === subPillarItem.id;
                        return (
                          <Button
                            key={subPillarItem.id}
                            onClick={() => {
                              if (filteredEventList.length === 0) {
                                setIsPillarTransitioning(true);
                              }
                              setSelectedSubPillar(subPillarItem);
                            }}
                            size="small"
                            disableElevation
                            sx={{
                              height: 25,
                              py: 0,
                              px: 1,
                              pt: '1px',
                              minWidth: 'auto',
                              fontSize: 'var(--font-size-md)',
                              lineHeight: 1,
                              textTransform: 'none',
                              borderRadius: '6px 6px 0px 0px',
                              color: isSelected ? 'white' : 'text.secondary',
                              bgcolor: isSelected ? 'var(--color-primary)' : 'transparent',
                              border: 'none',
                              fontWeight: 600,
                              fontFamily: 'var(--font-family-heading) !important',
                              borderRight: '1px solid rgba(0, 0, 0, 0.06)',
                              // opacity: subPillarItem.is_visible ? 1 : 0.5, // No longer needed
                              '&:hover': {
                                bgcolor: isSelected ? 'primary.dark' : 'grey.200',
                                border: 'none',
                                // cursor: subPillarItem.is_visible ? 'pointer' : 'default', // No longer needed
                              }
                            }}
                          >
                            {subPillarItem.name}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {panelContent.analysis}
              </div>
            </Panel>
            <ResizeHandle onDraggingChange={setIsResizing} />
            <Panel id="map-panel" defaultSize={mapPanelSize} minSize={30}>
              {panelContent.map}
            </Panel>
            <ResizeHandle onDraggingChange={setIsResizing} />
            <Panel id="events-panel" 
                  defaultSize={eventsPanelSize} 
                  minSize={15} 
                  collapsible={true} 
                  onCollapse={() => { 
                      setEventsPanelCollapsed(true);
                      saveLayout([analysisPanelSize, mapPanelSize, 0]); 
                  }}
                  onExpand={() => { 
                      setEventsPanelCollapsed(false);
                      const defaultEventSize = 20;
                      const minMapSize = 30;
                      const currentAnalysisSize = analysisPanelSize;
                      let newMapSize = 100 - currentAnalysisSize - defaultEventSize;
                      let finalEventSize = defaultEventSize;
                      if (newMapSize < minMapSize) {
                        newMapSize = minMapSize;
                        finalEventSize = 100 - currentAnalysisSize - newMapSize;
                      }
                      const newLayout = [
                        Number.isFinite(currentAnalysisSize) ? currentAnalysisSize : 25,
                        Number.isFinite(newMapSize) ? newMapSize : 50,
                        Number.isFinite(finalEventSize) ? finalEventSize : 25
                      ];

                      setMapPanelSize(newMapSize);
                      setEventsPanelSize(finalEventSize);
                      saveLayout(newLayout);
                  }}
                  collapsedSize={0}
                  collapsed={eventsPanelCollapsed ? "true" : undefined}>
              {panelContent.eventsList}
            </Panel>
            </PanelGroup>
          </PanelErrorBoundary>
          {/* Add the expand tab button, visible only AFTER initialization AND when collapsed on large screens */}
          {isLayoutInitialized && eventsPanelCollapsed && screenSize === SCREEN_SIZES.LARGE && (
            <Button
              className="expand-tab-button"
              variant="contained"
              size="small"
              onClick={() => {
                const panelGroup = largePanelGroupRef.current;
                if (panelGroup) {
                  // 1. Define the target size for the events panel
                  const defaultEventSize = 23;
                  
                  // 2. Get current sizes from state (or could use panelGroup.getLayout())
                  const currentAnalysisSize = analysisPanelSize;
                  
                  // 3. Calculate new layout, ensuring total is 100
                  // Shrink map panel to accommodate the events panel, respecting map's minSize
                  const minMapSize = 30; // As defined in the map panel props
                  let newMapSize = 100 - currentAnalysisSize - defaultEventSize;
                  let finalEventSize = defaultEventSize;

                  if (newMapSize < minMapSize) {
                    newMapSize = minMapSize;
                    finalEventSize = 100 - currentAnalysisSize - newMapSize;
                  }

                  const newLayout = [
                    Number.isFinite(currentAnalysisSize) ? currentAnalysisSize : 25,
                    Number.isFinite(newMapSize) ? newMapSize : 50,
                    Number.isFinite(finalEventSize) ? finalEventSize : 25
                  ];

                  // 4. Call setLayout imperatively
                  panelGroup.setLayout(newLayout);
                  
                  // 5. Update state AFTER imperative call
                  setEventsPanelCollapsed(false);
                  // No need to update analysisPanelSize as it didn't change
                  setMapPanelSize(newMapSize); // Update map size state
                  setEventsPanelSize(finalEventSize); // Update event size state
                  
                  // 6. Save layout (optional, but safer)
                  saveLayout(newLayout);
                }
              }} 
              sx={{ 
                position: 'absolute',
                right: 17, // Keep the adjusted value
                top: 163,
                transform: 'translateY(-50%) rotate(90deg)',
                transformOrigin: 'center right', 
                zIndex: 11, 
                borderBottomLeftRadius: 10,
                borderBottomRightRadius: 10,
                borderTopRightRadius: 0, 
                borderTopLeftRadius: 0,
                minWidth: 'auto',
                padding: '8px 16px', 
                letterSpacing: '0.5px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                fontFamily: 'var(--font-family-heading) !important',
                fontWeight: 'bold',
                fontSize: 'var(--font-size-md)',
                backgroundColor: 'var(--color-primary)',
                '&:hover': {
                  backgroundColor: 'var(--color-primary-dark, var(--color-primary))',
                }
              }}
            >
              Recent Events
            </Button>
          )}
        </div>
      );
    }
  };

  // Memoize mapConfiguration to only change when mapOptions actually change
  // CRITICAL: Don't return config until API has loaded view-specific options (not defaults)
  const memoizedMapConfiguration = useMemo(() => {
    // Wait until view-specific data is loaded (not just general settings)
    if (!isMapViewDataLoaded) {
      return null;
    }
    if (mapData?.mapOptions) {
      return {
        ...mapData.mapOptions,
        zoom: parseFloat(mapData.mapOptions.zoom),
        minZoom: parseFloat(mapData.mapOptions.minZoom)
      };
    }
    return null;
  }, [mapData?.mapOptions, isMapViewDataLoaded]);

  // Remove the intermediate state effect - use memoized config directly
  // The mapConfiguration state is now directly replaced by memoizedMapConfiguration



  // Reset states when dataset changes
  useEffect(() => {
    setIsMapLayersReady(false);
    setFilteredEventList([]);
  }, [selectedViewCode]);

  // Update URL when view changes (but not on initial load)
  useEffect(() => {
    if (!selectedViewCode) return;
    
    const currentView = view?.toLowerCase();
    const targetView = selectedViewCode.toLowerCase();
    
    if (currentView !== targetView) {
      // IMPORTANT: If embedded as org dashboard (orgIframe), stay on root view path
      // Navigating to /org/... inside the iframe re-renders OrganizationDashboard again (infinity mirror)
      const orgIframe = urlParams.get('orgIframe') === 'true';
      if (orgIframe) {
        navigate(`/${targetView}`);
      } else {
        navigate(`/${targetView}`);
      }
    }
  }, [selectedViewCode, navigate, view]);

  // Fetch views data
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    const fetchViews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/views`, { signal });
        if (!response.ok) throw new Error('Failed to fetch views');
        const data = await response.json();
        // Filter out invisible views unless force view is enabled
        const forceView = new URLSearchParams(window.location.search).get('force') === 'true';
        const visibleViews = forceView ? data : data.filter(view => view.is_visible);
        setAvailableViews(visibleViews);
        
        // Get view from URL parameter
        const viewFromUrl = view?.toUpperCase();
        
        if (viewFromUrl) {
          // Resolve view using flexible matching to avoid landing redirects
          const viewData = resolveViewFromParam(visibleViews, viewFromUrl);
          
          if (viewData) {
            // Set both the view code and data
            setSelectedViewCode(viewData.iso3);
            setSelectedViewData(viewData);
          } else {
            console.warn('View not found:', viewFromUrl);
            // If embedded as org dashboard, keep path and just clear selection
            const orgSlug = new URLSearchParams(window.location.search).get('orgSlug');
            const orgIframe = new URLSearchParams(window.location.search).get('orgIframe') === 'true';
            if (orgSlug && orgIframe) {
              setSelectedViewCode('');
              setSelectedViewData(null);
            } else {
              navigate('/');
            }
          }
        } else {
          // No view parameter, redirect to landing page
          const orgSlug = new URLSearchParams(window.location.search).get('orgSlug');
          const orgIframe = new URLSearchParams(window.location.search).get('orgIframe') === 'true';
          if (!(orgSlug && orgIframe)) {
            navigate('/');
          }
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error('Error fetching views:', error);
          setError(error.message);
        }
      }
    };

    fetchViews();
    
    // Cleanup function to abort ongoing fetch request
    return () => {
      controller.abort();
    };
  }, [view, navigate]); // Depend on view parameter

  // Handle URL updates separately with a ref to prevent additional renders
  const prevViewRef = useRef(view);
  useEffect(() => {
    // Skip if view hasn't changed
    if (prevViewRef.current === view) return;
    
    // Skip if we don't have views yet
    if (!availableViews.length) return;
    
    prevViewRef.current = view;
    const viewFromUrl = view?.toUpperCase();
    
    if (viewFromUrl) {
      const viewData = resolveViewFromParam(availableViews, viewFromUrl);
      
      if (viewData && viewData.iso3 !== selectedViewCode) {
        setSelectedViewCode(viewData.iso3);
        setSelectedViewData(viewData);
      } else if (!viewData) {
        // In org iframe context, do not redirect; otherwise go home
        const orgSlug = new URLSearchParams(window.location.search).get('orgSlug');
        const orgIframe = new URLSearchParams(window.location.search).get('orgIframe') === 'true';
        if (!(orgSlug && orgIframe)) {
          navigate('/');
        }
      }
    }
  }, [view, availableViews, selectedViewCode, navigate]);

  // New: Only reset pillar when view changes (CONSOLIDATED)
  const lastViewCodeRef = useRef();
  const shouldSelectDefaultParentRef = useRef(false);

  // CONSOLIDATED: When view changes, reset pillar states and fetch all pillar data
  useEffect(() => {
    if (selectedViewData && selectedViewData.id !== lastViewCodeRef.current) {
      
      // Clear analysis data immediately to prevent stale data
      if (refreshAnalysisData) {
        refreshAnalysisData();
      }
      
      setIsLoadingAllPillars(true);
      setAllPillarsError(null);
      setAllViewPillars([]); 

      startTransition(() => {
        setSelectedPillarId(null);
        setSelectedSubPillar(null);
        setParentPillar(null);
        setSubPillars([]);
        setFilteredEventList([]);
        setIsInitialPillarSet(false);
        setIsStateStabilized(false);
        initialSelectionPerformedRef.current = false;
      });
      
      shouldSelectDefaultParentRef.current = true;
      lastViewCodeRef.current = selectedViewData.id;

      const fetchAllPillarsForView = async () => {
      // CLEAR STATES IMMEDIATELY TO PREVENT STALE DATA DURING FETCH
      setIsInitialPillarSet(false);
      initialSelectionPerformedRef.current = false;
      shouldSelectDefaultParentRef.current = true;
      
      try {
        const apiUrl = `${API_URL}/api/pillars/all?viewId=${selectedViewData.id}`;
        console.log('🏛️ [PILLAR FETCH] Starting fetch for view:', selectedViewData.id);
        const response = await fetch(apiUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch all pillars for view ${selectedViewData.id}. Status: ${response.status}`);
          }
          
          const rawPillars = await response.json();
          
          // NUCLEAR SAFETY: Sanitize API response immediately 
          console.log('🛡️ [API SAFETY] Sanitizing pillars from API response:', {
            rawType: typeof rawPillars,
            rawIsArray: Array.isArray(rawPillars),
            rawLength: rawPillars?.length || 0
          });
          
          if (!Array.isArray(rawPillars)) {
            console.error('❌ [API SAFETY] API response is not an array:', typeof rawPillars);
            setAllViewPillars([]);
            setAllPillarsError('Invalid API response format');
            return;
          }
          
          const safePillars = rawPillars.filter((pillar, index) => {
            try {
              if (!pillar || typeof pillar !== 'object') {
                console.warn(`❌ [API SAFETY] Non-object pillar at index ${index}:`, typeof pillar);
                return false;
              }
              
              // Check for React component contamination
              if (pillar.$$typeof || pillar._owner || pillar.type || pillar.props || pillar.ref) {
                console.error(`❌ [API SAFETY] React component in API response at index ${index}:`, {
                  name: pillar.name || 'Unknown',
                  id: pillar.id || 'Unknown',
                  $$typeof: !!pillar.$$typeof,
                  _owner: !!pillar._owner,
                  type: !!pillar.type
                });
                return false;
              }
              
              // Check for React symbols
              if (pillar.$$typeof === Symbol.for('react.element') || 
                  pillar.$$typeof === Symbol.for('react.memo') ||
                  pillar.$$typeof === Symbol.for('react.forward_ref')) {
                console.error(`❌ [API SAFETY] React symbol in API response at index ${index}`);
                return false;
              }
              
              return true;
            } catch (error) {
              console.error(`❌ [API SAFETY] Error validating API pillar at index ${index}:`, error);
              return false;
            }
          });
          
          if (safePillars.length !== rawPillars.length) {
            console.warn(`⚠️ [API SAFETY] Filtered out ${rawPillars.length - safePillars.length} contaminated pillars from API response`);
          }
          
          console.log('✅ [API SAFETY] API response sanitization complete:', {
            originalCount: rawPillars.length,
            safeCount: safePillars.length,
            removedCount: rawPillars.length - safePillars.length
          });
          
          setAllViewPillars(safePillars);
          
          // Apply organization pillar filtering if this is an organization dashboard
          filterPillarsForOrganization(safePillars);
        } catch (error) {
          console.error('Error fetching pillars:', error.message);
          setAllPillarsError(error.message);
          setAllViewPillars([]); 
        } finally {
          setIsLoadingAllPillars(false);
        }
      };
      fetchAllPillarsForView();
    } else if (!selectedViewData) {
      // No view selected, clear all pillar related states
      startTransition(() => {
        setAllViewPillars([]);
        setSelectedPillarId(null);
        setSelectedSubPillar(null);
        setParentPillar(null);
        setSubPillars([]);
        setFilteredEventList([]);
        setIsInitialPillarSet(false);
      });
      shouldSelectDefaultParentRef.current = false;
    }
  }, [selectedViewData, refreshAnalysisData]); // Only depend on selectedViewData changes

  // CONSOLIDATED: Select initial default PARENT pillar once filteredPillars are loaded
  // RESPECTS VISUAL ORDER: Always selects the first pillar in visual order for initial load
  // to meet user expectations (analysis type prioritization only applies to auto-selection after changes)
  useEffect(() => {
    if (isLoadingAllPillars || allPillarsError || filteredPillars.length === 0 || !shouldSelectDefaultParentRef.current) {
      return; 
    }
    
    // CRITICAL: Don't override user's manual selection
    // Once initial selection is performed, NEVER run this again
    if (initialSelectionPerformedRef.current) {
      console.log('🔒 [INITIAL SELECTION] Skipping - initial selection already performed');
      shouldSelectDefaultParentRef.current = false;
      return;
    }
    
    if (selectedPillarId !== null) {
      console.log('🔒 [INITIAL SELECTION] Skipping - user has already selected a pillar:', selectedPillarId);
      shouldSelectDefaultParentRef.current = false;
      return;
    }
    
    const parentPillars = filteredPillars.filter(p => !p.parent_pillar_id);
    if (parentPillars.length === 0) {
      shouldSelectDefaultParentRef.current = false; 
      // No selectable parent pillars; keep initial setup false to avoid hooks running with null ids
      setIsInitialPillarSet(false);
      return;
    }

    const sortedParentPillars = [...parentPillars].sort((a, b) => {
      // INITIAL SELECTION: Respect visual order first (custom_order, order_index, name)
      // Users expect the first visual box to be selected, regardless of analysis type
      const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
      const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
      if (aCustom !== bCustom) return aCustom - bCustom;
      const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
      const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.name || '').localeCompare(b.name || '');
    });

    const initialSelectedParentPillarData = sortedParentPillars[0];

    if (initialSelectedParentPillarData) {
      console.log('🔧 [INITIAL SELECTION] Selected first pillar in visual order:', {
        name: initialSelectedParentPillarData.name,
        id: initialSelectedParentPillarData.id,
        showsOwnAnalysis: initialSelectedParentPillarData.is_visible,
        custom_order: initialSelectedParentPillarData.custom_order,
        selectionReason: 'First in visual order (respects user expectations)'
      });
      
      startTransition(() => {
        setSelectedPillarId(initialSelectedParentPillarData.id);
        initialSelectionPerformedRef.current = true;
      });
      // IMPORTANT: Do not mark isInitialPillarSet=true until selectedPillarId is actually non-null.
      // The pillar sync effect will set isInitialPillarSet once IDs are ready.
      setIsInitialPillarSet(false);
    }
    shouldSelectDefaultParentRef.current = false;

  }, [filteredPillars, isLoadingAllPillars, allPillarsError]);

  // Track previous values to prevent unnecessary runs
  const prevAutoSelectionStateRef = useRef({
    selectedPillarId: null,
    filteredPillarsLength: 0,
    isStateStabilized: false
  });

  // NEW: Auto-select next available pillar when current selection becomes unavailable  
  // PRIORITIZES VISIBLE PILLARS: For auto-selection after changes (not initial load),
  // prefer pillars that show their own analysis (is_visible = true) to avoid confusion
  useEffect(() => {
    const currentState = {
      selectedPillarId,
      filteredPillarsLength: filteredPillars.length,
      isStateStabilized
    };
    
    // Check if any relevant state actually changed
    const stateChanged = Object.keys(currentState).some(key => 
      currentState[key] !== prevAutoSelectionStateRef.current[key]
    );
    
    if (!stateChanged) {
      return; // Skip if no relevant state changed
    }
    
    // Update previous state
    prevAutoSelectionStateRef.current = currentState;
    
    // Enhanced: Run when we have filtered pillars and a selected pillar that's no longer available
    // Removed the isInitialPillarSet guard to make this more responsive to pillar toggles
    if (!selectedPillarId || isLoadingAllPillars || filteredPillars.length === 0) {
      return;
    }
    
    // RACE CONDITION GUARD: Check if auto-selection should be suppressed due to recent pillar toggle
    if (window.pillarToggleAutoSelectionSuppress && Date.now() < window.pillarToggleAutoSelectionSuppress) {
      console.log('🛡️ ACTIVE DATA: Auto-selection suppressed due to recent pillar toggle - preventing race condition');
      return;
    }
    
    // RESET OPERATION GUARD: Don't auto-select if this is triggered by a reset operation
    if (localOrganizationSettings?.resetOperation) {
      console.log('🛡️ RESET: Auto-selection suppressed during reset operation');
      return;
    }
    
    // DOM RACE CONDITION GUARD: Prevent auto-selection during layout initialization to avoid DOM conflicts
    if (!isLayoutInitialized && screenSize === SCREEN_SIZES.LARGE) {
      console.log('🛡️ LAYOUT: Auto-selection suppressed during layout initialization - preventing DOM race condition');
      return;
    }
    
    // STATE STABILIZATION GUARD: Prevent auto-selection during state stabilization period
    if (!isStateStabilized) {
      return;
    }
    
    /* 
     * AUTO-SELECTION AVAILABILITY CHECK:
     * 
     * This works in conjunction with the collapsible pillar UI to ensure that
     * when a parent pillar is collapsed in OrganizationDashboard, any selected
     * sub-pillar becomes "unavailable" and triggers auto-selection of a new pillar.
     * 
     * AVAILABILITY RULES:
     * - Parent pillars: Always available if they're in filteredPillars
     * - Sub-pillars: Only available if their parent is currently expanded in the UI
     * 
     * This ensures the analysis follows the visual state - collapsed folders
     * hide their children from both the UI and the data analysis.
     */
    const selectedPillar = filteredPillars.find(p => p.id === selectedPillarId);
    
    // CRITICAL FIX: Only check if pillar exists in filteredPillars
    // Don't use expandedPillars state which may not be synced in org dashboard iframe
    const currentPillarStillAvailable = !!selectedPillar;
    
    // Only auto-select if the current pillar is completely missing from filteredPillars
    if (!currentPillarStillAvailable) {
      console.log('ACTIVE DATA: Selected pillar', selectedPillarId, 'hidden, auto-selecting replacement');
      
      // Clear any suppression flag immediately since current pillar is no longer available
      delete window.pillarToggleAutoSelectionSuppress;
      
      // Find the next available parent pillar
      const parentPillars = filteredPillars.filter(p => !p.parent_pillar_id);
      if (parentPillars.length > 0) {
        const sortedParentPillars = [...parentPillars].sort((a, b) => {
          // PRIORITIZE VISIBLE PILLARS: Prefer pillars that show their own analysis (is_visible = true)
          // This prevents auto-selecting to pillars that immediately redirect to sub-pillar analysis
          if (a.is_visible !== b.is_visible) {
            return b.is_visible ? 1 : -1; // is_visible = true comes first
          }
          
          // Then sort by custom_order, order_index, name as before
          const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
          const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
          if (aCustom !== bCustom) return aCustom - bCustom;
          const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
          const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return (a.name || '').localeCompare(b.name || '');
        });

        const nextPillar = sortedParentPillars[0];
        
        // Immediate auto-selection for instant feedback
        setSelectedPillarId(nextPillar.id);
        // Clear sub-pillar since we're switching to a new parent
        setSelectedSubPillar(null);
        console.log('ACTIVE DATA: Post-change auto-selection completed - active pillar should be', nextPillar.name);
      } else {
        // No parent pillars available, clear selection
        console.log('ACTIVE DATA: No pillars available, clearing selection');
        
        setSelectedPillarId(null);
        setSelectedSubPillar(null);
        setParentPillar(null);
        setSubPillars([]);
      }
    }
  }, [filteredPillars, selectedPillarId, isLoadingAllPillars, localOrganizationSettings, isLayoutInitialized, screenSize, isSafeMounted, isStateStabilized]);

  // CONSOLIDATED: Update parentPillar, subPillars, auto-select subPillar, and set events (STREAMLINED)
  useEffect(() => {
    if (!selectedPillarId || !selectedViewData?.id || filteredPillars.length === 0) {
      if (!selectedPillarId && !isLoadingAllPillars) {
        startTransition(() => {
          setParentPillar(null);
          setSubPillars([]);
          setSelectedSubPillar(null);
          setFilteredEventList([]);
        });
      }
      return;
    }

    // RACE CONDITION GUARD: Check if sub-pillar logic should be suppressed due to recent pillar toggle
    if (window.pillarToggleAutoSelectionSuppress && Date.now() < window.pillarToggleAutoSelectionSuppress) {
      console.log('🛡️ SUB-PILLAR: Sub-pillar auto-selection suppressed due to recent pillar toggle - preventing race condition');
      return;
    }
    
    // RESET OPERATION GUARD: Don't auto-select sub-pillars if this is triggered by a reset operation
    if (localOrganizationSettings?.resetOperation) {
      console.log('🛡️ RESET: Sub-pillar auto-selection suppressed during reset operation');
      return;
    }

    const newParentPillarData = filteredPillars.find(p => p.id === selectedPillarId);

    if (newParentPillarData) {
      const childPillars = filteredPillars.filter(p => p.parent_pillar_id === newParentPillarData.id);
      const sortedChildPillars = [...childPillars].sort((a, b) => {
        const aCustom = typeof a.custom_order === 'number' ? a.custom_order : Infinity;
        const bCustom = typeof b.custom_order === 'number' ? b.custom_order : Infinity;
        if (aCustom !== bCustom) return aCustom - bCustom;
        const aOrder = typeof a.order_index === 'number' ? a.order_index : Infinity;
        const bOrder = typeof b.order_index === 'number' ? b.order_index : Infinity;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      let newSelectedSubPillarState = selectedSubPillar; 

      if (newParentPillarData.is_visible === false) {
        // Check if current selectedSubPillar is valid for this parent
        const isCurrentSubPillarValid = selectedSubPillar && 
          selectedSubPillar.parent_pillar_id === newParentPillarData.id && 
          selectedSubPillar.is_visible === true;
          
        if (isCurrentSubPillarValid) {
          newSelectedSubPillarState = selectedSubPillar;
        } else {
          // Only auto-select if no valid manual selection exists
          const firstVisibleSub = sortedChildPillars.find(sp => sp.is_visible === true);
          if (firstVisibleSub) {
            newSelectedSubPillarState = firstVisibleSub;
          } else {
            console.log('⚠️ No visible sub-pillars found for invisible parent pillar');
            newSelectedSubPillarState = null;
          }
        }
      } else {
        if (selectedSubPillar && selectedSubPillar.parent_pillar_id !== newParentPillarData.id) {
          newSelectedSubPillarState = null;
        } else if (selectedSubPillar) {
        } else {
          newSelectedSubPillarState = null;
        }
      }
      
      startTransition(() => {
        setParentPillar(newParentPillarData);
        setSubPillars(sortedChildPillars);
        setSelectedSubPillar(newSelectedSubPillarState);
        
        // Events are now handled by the event sync effect, no need to set them here
        
        // Mark initial pillar setup as complete
        if (!isInitialPillarSet) {
          setIsInitialPillarSet(true);
        }
      });
    } else {
      startTransition(() => {
        setParentPillar(null);
        setSubPillars([]);
        setSelectedSubPillar(null);
        setFilteredEventList([]);
        // Do not mark initial setup complete here; only mark it once a valid pillar is resolved
        if (!selectedPillarId && isInitialPillarSet) {
          setIsInitialPillarSet(false);
        }
      });
    }
  }, [selectedPillarId, filteredPillars, selectedViewData?.id, activeData, isInitialPillarSet]); // Removed selectedSubPillar to prevent auto-selection override

  // Optimized debounced event list update
  const debouncedEventListUpdate = useCallback(
    debounce((newEventList, pillarId) => {
      setFilteredEventList(newEventList);
      // Use a slight delay to ensure smooth transition completion
      setTimeout(() => {
        setIsPillarTransitioning(false);
      }, 50); // Small delay to ensure CSS transitions have time to complete
    }, 100), // Reduced delay for faster response
    []
  );

  // Optimized event sync with memoization and reduced processing
  const currentEventIds = useMemo(() => 
    filteredEventList.map(e => e.id).sort().join(','), 
    [filteredEventList]
  );

  useEffect(() => {
    // Only process when we have the necessary data
    if (!isInitialPillarSet || !eventsPillarId) {
      if (!eventsPillarId && filteredEventList.length > 0) {
        setFilteredEventList([]);
        setIsPillarTransitioning(false);
      }
      return;
    }

    if (activeData.length === 0) {
      return; // Wait for data
    }

    // Filter events efficiently
    const newEventList = activeData.filter(event => event.pillar_id === eventsPillarId);
    
    // Fast comparison using IDs
    const newEventIds = newEventList.map(e => e.id).sort().join(',');
    const eventsChanged = currentEventIds !== newEventIds;
    
    if (!eventsChanged) {
      // Clear loading state if events are the same but we're still transitioning
      if (isPillarTransitioning) {
        setIsPillarTransitioning(false);
      }
      return;
    }

    // Determine if this is a sub-pillar change (parent pillar unchanged)
    const isSubPillarChange = eventsPillarId === previousEventsPillarId && 
                             selectedPillarId === eventsPillarId && 
                             selectedSubPillar;

    // Handle different transition types
    if (newEventList.length === 0 && filteredEventList.length > 0) {
      // Keep current events until new data loads
      setIsPillarTransitioning(true);
    } else if (!isSubPillarChange) {
      // Full pillar change - update events and show transition
      setIsPillarTransitioning(true);
      debouncedEventListUpdate(newEventList, eventsPillarId);
    } else {
      // Sub-pillar change - keep current events visible
      // Only update if we have new events to show
      if (newEventList.length > 0) {
        debouncedEventListUpdate(newEventList, eventsPillarId);
      }
    }
  }, [activeData, eventsPillarId, isInitialPillarSet, currentEventIds, selectedPillarId, selectedSubPillar, previousEventsPillarId, debouncedEventListUpdate, isPillarTransitioning, filteredEventList.length]);

  // Track previous pillar ID for sub-pillar change detection
  useEffect(() => {
    if (!isPillarTransitioning && eventsPillarId && eventsPillarId !== previousEventsPillarId) {
      setPreviousEventsPillarId(eventsPillarId);
    }
  }, [eventsPillarId, isPillarTransitioning, previousEventsPillarId]);



  /* 
   * CRITICAL PILLAR ANALYSIS LOGIC:
   * 
   * This function determines whether to show analysis for the PARENT pillar or a SUB-PILLAR
   * based on the ORIGINAL PILLAR VISIBILITY DATA (is_visible field from the database).
   * 
   * DECISION TREE:
   * 1. If parent.is_visible === true  → Show PARENT PILLAR analysis (view-level data)
   * 2. If parent.is_visible === false → Show FIRST VISIBLE SUB-PILLAR analysis (sub-region data)
   * 
   * This ensures that:
   * - When a parent pillar represents view-wide data, we show that broader analysis
   * - When a parent pillar is just a grouping mechanism, we dive into the specific sub-pillar data
   * 
   * Examples:
   * - "Protection" parent (visible) → Shows view-wide protection analysis
   * - "Information & Communication" parent (hidden) → Shows first visible sub-pillar like "Accountability to Affected People" analysis
   */
  const handleBoxClick = useCallback((item) => {
    if (!item || typeof item.pillarId === 'undefined') {
      return;
    }

    const clickedParentData = filteredPillars.find(p => p.id === item.pillarId);
    if (!clickedParentData) {
      return;
    }

    // If the clicked parent is already active
    if (selectedPillarId === item.pillarId) {
      if (clickedParentData.is_visible === true && selectedSubPillar) {
        // PARENT VISIBLE: Clicking the currently active visible parent should just clear the sub-pillar
        // This switches from sub-pillar analysis back to parent-level analysis
        setIsPillarTransitioning(true);
        setSelectedSubPillar(null);
      }
      return;
    }

    // Set transitioning state immediately for user feedback
    setIsPillarTransitioning(true);

    // CORE LOGIC: SWITCH TO THE NEW PARENT
    if (clickedParentData.is_visible === false) {
      // PARENT HIDDEN: This parent is a grouping mechanism, not a data source
      // → Auto-select first visible child for actual analysis data
      const childPillars = filteredPillars.filter(p => p.parent_pillar_id === clickedParentData.id);
      const firstVisibleSub = childPillars.find(sp => sp.is_visible === true);

      setSelectedPillarId(item.pillarId);
      setParentPillar(clickedParentData);
      setSubPillars(childPillars);
      setSelectedSubPillar(firstVisibleSub || null); // Analysis will use the sub-pillar data
    } else {
      // PARENT VISIBLE: This parent has its own view-level data
      // → Show parent-level analysis, clear any sub-pillar
      setSelectedPillarId(item.pillarId);
      setSelectedSubPillar(null); // Analysis will use the parent pillar data
    }
  }, [selectedPillarId, selectedSubPillar, filteredPillars]);

  // Determine the active pillar key for DTM display (always a string key)
  const activePillarKeyForDTM = useMemo(() => {
    if (selectedSubPillar) return selectedSubPillar.key;
    return selectedPillar?.key;
  }, [selectedPillar, selectedSubPillar]);

  const handleEventTypeToggle = useCallback((eventType) => {
    setSelectedEventTypes(prev => 
      prev.includes(eventType)
        ? prev.filter(type => type !== eventType)
        : [...prev, eventType]
    );
  }, []);

  const handleResetEventTypes = useCallback(() => {
    setSelectedEventTypes([]);
  }, []);

  // Memoize filter functions
  const { filterAcledEvents, filterTimeChartData } = useEventFilters(
    selectedEventTypes,
    selectedRegion,
    selectedTimeRange,
    selectedActorType,
    selectedEventCategories
  );

  const handleTimeRangeChange = useCallback((newRange) => {
    setSelectedTimeRange(newRange);
  }, []);

  const handleActorSelect = useCallback((actor) => {
    setSelectedActorType(prevActor => prevActor === actor ? null : actor);
  }, []);

  // Calculate time chart data from ACLED events
  const timechartData = useMemo(() => {
    return filterTimeChartData(acledData || []);
  }, [acledData, filterTimeChartData]);

  // Calculate filtered ACLED data with memoization to prevent unnecessary updates
  const filteredAcledData = useMemo(() => {
    return filterAcledEvents(acledData || []);
  }, [acledData, filterAcledEvents]);
  
  // Direct connection to useAcledHeatmap
  useAcledHeatmap(filteredAcledData, activePillarKeyForDTM, setMapLayers);

  // Optimize interaction chart updates during slider movement
  const debouncedUpdateInteractionChart = useCallback(
    debounce((newData) => {
      setInteractionChartData(newData);
    }, 100),
    []
  );

  // Update interaction chart data when filtered data changes, but only if it's actually different
  useEffect(() => {
    // The current implementation using random sampling might not be reliable
    // Instead, directly set the filtered ACLED data to the interaction chart
    // Ensure interactionChartData is always an array
    setInteractionChartData(filteredAcledData || []);
  }, [filteredAcledData]); // Dependency only on filteredAcledData

  // Memoize formatting functions
  const formatEventCount = useCallback((count) => 
    formatButtonValue(count, count >= 1000 ? 'thousands' : 'number'), 
  []);

  const formatFatalityCount = useCallback((count) => 
    formatButtonValue(count, count >= 1000 ? 'thousands' : 'number'), 
  []);

  const totalEvents = filteredAcledData.length;
  const totalFatalities = filteredAcledData.reduce((sum, event) => sum + (event.fatalities || 0), 0);

  const handleViewToggle = useCallback((dataset) => {
    const view = availableViews.find(c => c.iso3.toLowerCase() === dataset.toLowerCase());
    
    if (!view) {
      console.error('View not found:', dataset);
      return;
    }
    
    // Batch all state updates in one render cycle using startTransition
    startTransition(() => {
      setIsMapVisible(false);
      setSelectedRegion(null);
      setSelectedActorType(null);
      setSelectedViewData(view);
      setSelectedViewCode(view.iso3);
      // Reset pillar selection
      setSelectedPillarId(null);
      setSelectedSubPillar(null);
      setParentPillar(null);
      setSubPillars([]);
      setFilteredEventList([]);
    });

    if (eventListScrollRef.current) {
      eventListScrollRef.current.scrollTop = 0;
    }
  }, [availableViews, eventListScrollRef]);

  useEffect(() => {
    if (isMapViewDataLoaded && memoizedMapConfiguration && isMapLayersReady) {
      setIsMapVisible(true);
    }
  }, [isMapViewDataLoaded, memoizedMapConfiguration, isMapLayersReady]);

  const handleCategoryToggle = useCallback((category) => {
    setSelectedEventCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  }, []);

  // Memoize adjustMapOptionsForScreen
  const adjustMapOptionsForScreen = useCallback((options, datasetKey) => {
    const isSmallScreen = window.innerWidth < 768;
    let adjustedOptions = { ...options };
    
    if (isSmallScreen) {
      adjustedOptions.zoom = Math.max(3, options.zoom - 1);
    }

    return adjustedOptions;
  }, []);

  const handleLocationSelect = useCallback(async (location) => {
    
    // Function to wait for heatmap update
    const waitForHeatmapUpdate = () => new Promise(resolve => {
      // Use a MutationObserver to detect when the heatmap layer is added/updated
      const observer = new MutationObserver((mutations, obs) => {
        const heatmapCanvas = document.querySelector('canvas.ol-layer:last-child');
        if (heatmapCanvas) {
          obs.disconnect();
          resolve();
        }
      });

      observer.observe(document.querySelector('.map-container'), {
        childList: true,
        subtree: true
      });

      // Set a timeout to resolve anyway after 100ms in case the observer doesn't catch the update
      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 100);
    });

    if (!location) {
      setSelectedRegion(null);

      waitForHeatmapUpdate().then(() => {
        mapRef.current?.flyToDefault();
      });
      return;
    }

    try {
      
      // Get the numeric ISO code for the view
      const isoResponse = await fetch(`/api/views/${location.view_code}/iso-code`);
      if (!isoResponse.ok) {
        throw new Error('Failed to fetch view ISO code');
      }
      const { iso_numeric } = await isoResponse.json();

      // Set default date range if not provided
      const startDate = location.start_date || new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0];
      const endDate = location.end_date || new Date().toISOString().split('T')[0];

      // Fetch ACLED data using the numeric ISO code with valid date parameters
      const acledResponse = await fetch(`/api/acled?views=${iso_numeric}&start_date=${startDate}&end_date=${endDate}`);
      if (!acledResponse.ok) {
        throw new Error('Failed to fetch ACLED data');
      }
      const fetchedAcledData = await acledResponse.json();

      // Update the filtered ACLED data for the map
      // Instead of using setAcledData which doesn't exist, we'll use the existing ACLED data
      // and update the filtered data for the map visualization
      if (fetchedAcledData && Array.isArray(fetchedAcledData)) {
        // Update the interaction chart data with the new ACLED data
        setInteractionChartData(fetchedAcledData);
        
        // If you need to update the heatmap, you can use the useAcledHeatmap hook's functionality
        // This will be handled by the existing useEffect that depends on filteredAcledData
      }

      // First update the region state with the standardized name from fieldmaps
      const selectedRegion = {
        ...location,
        // Use the fieldmap name for display and filtering since our backend handles both names
        name: location.adm1_name || location.name
      };
      setSelectedRegion(selectedRegion);

      // Wait for the heatmap to update, then animate the map
      waitForHeatmapUpdate().then(() => {


        const zoomExtent = location.zoom_extent;
        if (zoomExtent && Array.isArray(zoomExtent) && zoomExtent.length === 4) {
          mapRef.current?.flyToExtent(zoomExtent, 700, location.adm1_name || location.name);
        } else {
          // Fallback to center-based navigation if extent is not available
          const centerLon = parseFloat(location.lon);
          const centerLat = parseFloat(location.lat);
          
          // Use extent-based approach instead of hardcoded zoom
          mapRef.current?.flyTo([centerLon, centerLat], 700, location.adm1_name || location.name);
        }
      });
    } catch (error) {
      console.error('Error in handleLocationSelect:', error);
      
      // Set the region state anyway
      setSelectedRegion({
        ...location,
        name: location.adm1_name || location.name
      });

      // Still try to navigate to the location
      waitForHeatmapUpdate().then(() => {
        const zoomExtent = location.zoom_extent;
        if (zoomExtent && Array.isArray(zoomExtent) && zoomExtent.length === 4) {
          mapRef.current?.flyToExtent(zoomExtent, 700, location.adm1_name || location.name);
        } else {
          // Fallback to center-based navigation if extent is not available
          const centerLon = parseFloat(location.lon);
          const centerLat = parseFloat(location.lat);
          
          // Use extent-based approach instead of hardcoded zoom
          mapRef.current?.flyTo([centerLon, centerLat], 700, location.adm1_name || location.name);
        }
      });
    }
  }, []);

  // Memoize map props to prevent unnecessary re-renders
  const mapProps = useMemo(() => ({
    view: selectedViewCode ? { iso3: selectedViewCode } : null,
    mapOptions: mapData.mapOptions,
    layers: showLayersOnMap ? mapLayers : mapLayers.filter(layer => 
      !['conflict', 'dtm'].includes(layer.id)
    ),
    showLegend: true,
    mapSettings: {
      // ... other settings
    },
    isLoading: !isMapLayersReady,
    selectedPillarKey: selectedPillarId,
    ref: mapRef,
  }), [
    selectedViewCode,
    mapData.mapOptions,
    mapLayers,
    showLayersOnMap,
    isMapLayersReady,
    selectedPillarId,
  ]);

  // STATE STABILIZATION: Only start the stabilization timer AFTER a pillar has been initially selected
  useEffect(() => {
    if (isSafeMounted && !isStateStabilized && initialSelectionPerformedRef.current) {
      console.log('🔄 [STATE STABILIZATION] Starting stabilization timer after initial selection');
      const timer = setTimeout(() => {
        console.log('✅ [STATE STABILIZATION] State stabilization period complete - enabling full reactivity');
        startTransition(() => {
          setIsStateStabilized(true);
        });
      }, 150); // Longer delay to prevent race conditions during initial data loading

      return () => clearTimeout(timer);
    }
  }, [isSafeMounted, isStateStabilized, initialSelectionPerformedRef.current]);

  // Initialize layout imperatively if screen is large and ref is available
  useEffect(() => {
    if (screenSize === SCREEN_SIZES.LARGE && largePanelGroupRef.current && !isLayoutInitialized && isSafeMounted && isStateStabilized) {
      // Add a small delay to ensure the panel group is fully mounted
      const timer = setTimeout(() => {
        try {
          if (largePanelGroupRef.current) {
            const currentLayout = largePanelGroupRef.current.getLayout();
            if (currentLayout && Array.isArray(currentLayout)) {
              const validatedPanels = currentLayout.map(size => 
                Number.isFinite(size) ? size : 25
              );
              largePanelGroupRef.current.setLayout(validatedPanels);
              
              console.log('✅ [LAYOUT INIT] Successfully initialized panel layout');
              // Use startTransition to batch the state update and prevent race conditions
              startTransition(() => {
                setIsLayoutInitialized(true);
              });
            }
          }
        } catch (error) {
          console.warn('🚨 [LAYOUT INIT] Failed to initialize layout:', error);
          // If initialization fails, we'll fall back to the default layout
          startTransition(() => {
            setIsLayoutInitialized(true);
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [screenSize, isLayoutInitialized, isSafeMounted, isStateStabilized]);
  
  // Memoize the refresh function to maintain stable reference
  const refreshDataAfterBatchCompletion = useCallback(() => {
    // Add timestamp tracking to avoid rapid consecutive calls
    const now = Date.now();
    const lastRefreshTime = refreshDataAfterBatchCompletion.lastCallTime || 0;
    
    // Skip if called within 500ms of last call
    if (now - lastRefreshTime < 500) {
      return;
    }
    
    // Update last call time
    refreshDataAfterBatchCompletion.lastCallTime = now;
    
    
    const { 
      refreshKPIData, 
      refreshEventData, 
      refreshAnalysisData, // Added
      refreshACLEDData,    // Added
      // refreshMapAnalysisData, // Placeholder - Add if useMapAnalysis hook provides it
      // refreshDtmData,       // Placeholder - Add if useDtmData hook provides it
    } = refreshAllDataRef.current || {};
    
    if (refreshKPIData) refreshKPIData();
    if (refreshEventData) refreshEventData();
    if (refreshAnalysisData) refreshAnalysisData();
    if (refreshACLEDData) refreshACLEDData();
    // if (refreshMapAnalysisData) setTimeout(() => refreshMapAnalysisData(), 100); // Example of staggering
    // if (refreshDtmData) setTimeout(() => refreshDtmData(), 200); // Example of staggering

  }, []);

  // Create a callback to refresh all data that has stable references
  const refreshAllDataRef = useRef(null);
  
  // Update the ref when the dependencies change
  useEffect(() => {
    refreshAllDataRef.current = {
      selectedViewData, // Keep for context if needed by refresh functions, though not directly used by them
      selectedPillarId,  // Keep for context
      refreshKPIData,
      refreshEventData,
      refreshAnalysisData, // Now correctly passed
      refreshACLEDData     // Now correctly passed
      // refreshMapAnalysisData, // Placeholder
      // refreshDtmData        // Placeholder
    };
  }, [
    selectedViewData, 
    selectedPillarId, 
    refreshKPIData, 
    refreshEventData, 
    refreshAnalysisData, // Now correctly passed
    refreshACLEDData     // Now correctly passed
    // refreshMapAnalysisData, // Placeholder
    // refreshDtmData        // Placeholder
  ]);

  // Insert the new ref-based event handling logic here, ensuring it's AFTER custom hook calls.
  // Refs for state and props needed by the batch task completion handler
  // IMPORTANT: These MUST be initialized AFTER the state/props they depend on (e.g., activeData) are declared from custom hooks.
  const selectedViewDataRef = useRef(selectedViewData);
  const activeDataRef = useRef(activeData);
  const selectedPillarIdRef = useRef(selectedPillarId);
  const analysisPillarIdRef = useRef(analysisPillarId);
  const refreshKPIDataRef = useRef(refreshKPIData);
  const refreshEventDataRef = useRef(refreshEventData);
  const refreshAnalysisDataRef = useRef(refreshAnalysisData);
  const refreshACLEDDataRef = useRef(refreshACLEDData);

  // Effects to keep refs updated with the latest state/prop values
  useEffect(() => { selectedViewDataRef.current = selectedViewData; }, [selectedViewData]);
  useEffect(() => { activeDataRef.current = activeData; }, [activeData]);
  useEffect(() => { selectedPillarIdRef.current = selectedPillarId; }, [selectedPillarId]);
  useEffect(() => { analysisPillarIdRef.current = analysisPillarId; }, [analysisPillarId]);
  useEffect(() => { refreshKPIDataRef.current = refreshKPIData; }, [refreshKPIData]);
  useEffect(() => { refreshEventDataRef.current = refreshEventData; }, [refreshEventData]);
  useEffect(() => { refreshAnalysisDataRef.current = refreshAnalysisData; }, [refreshAnalysisData]);
  useEffect(() => { refreshACLEDDataRef.current = refreshACLEDData; }, [refreshACLEDData]);

  // Ref to hold the latest version of the batch task processing logic
  const batchTaskProcessingLogicRef = useRef(null);

  // This useEffect updates the processing logic function stored in the ref
  // whenever any of its dependencies change.
  useEffect(() => {
    if (selectedViewData && activeData) { // Ensure foundational data is ready
      batchTaskProcessingLogicRef.current = (data) => {
        // Guard again, just in case, but refs should be up-to-date here
        if (!data || !selectedViewDataRef.current || !activeDataRef.current) {
      return;
    }
        const { viewId: taskViewInput, pillarId: taskPillarInput, dataType, taskDetails } = data;
      

        const currentSelectedViewIdStr = String(selectedViewDataRef.current.id);
        const currentSelectedViewISO3 = selectedViewDataRef.current.iso3;

        let viewMatches = false;
        if (taskDetails?.view_id) {
          viewMatches = String(taskDetails.view_id) === currentSelectedViewIdStr;
        } else if (taskDetails?.view_iso3) {
          viewMatches = taskDetails.view_iso3.toLowerCase() === currentSelectedViewISO3.toLowerCase();
        } else if (taskViewInput) {
          // Fallback to taskViewInput if specific IDs aren't in taskDetails
          viewMatches = String(taskViewInput) === currentSelectedViewIdStr || 
                           String(taskViewInput).toLowerCase() === currentSelectedViewISO3.toLowerCase() ||
                           String(taskViewInput).toLowerCase() === selectedViewDataRef.current.name.toLowerCase(); 
        }

        if (viewMatches) {
          
          const taskActualPillarId = taskDetails?.pillar_id ? String(taskDetails.pillar_id) : null;
          const currentPillarIdForEvents = selectedPillarIdRef.current ? String(selectedPillarIdRef.current) : null; 
          const currentPillarIdForAnalysis = analysisPillarIdRef.current ? String(analysisPillarIdRef.current) : null;

          switch (dataType) {
            case 'indicators':
              if (refreshKPIDataRef.current) refreshKPIDataRef.current(); 
              break;
            case 'events':
              if (taskActualPillarId && taskActualPillarId === currentPillarIdForEvents) {
                if (refreshEventDataRef.current) refreshEventDataRef.current();
              } else if (!taskActualPillarId && !currentPillarIdForEvents) { 
                if (refreshEventDataRef.current) refreshEventDataRef.current();
              } else {
              }
              break;
            case 'analysis':
              if (taskActualPillarId && taskActualPillarId === currentPillarIdForAnalysis) {
                if (refreshAnalysisDataRef.current) refreshAnalysisDataRef.current(); 
              } else if (!taskActualPillarId && !currentPillarIdForAnalysis) { 
                  if (refreshAnalysisDataRef.current) refreshAnalysisDataRef.current();
              } else {
        }
              break;
            case 'acled':
              if (refreshACLEDDataRef.current) refreshACLEDDataRef.current(); 
              break;
            case 'dtm':
              // Example: DTM might be tied to events pillar or analysis pillar depending on context
              const relevantDTMPillar = currentPillarIdForEvents || currentPillarIdForAnalysis;
              if (taskActualPillarId && taskActualPillarId === relevantDTMPillar) { 
              } else if (!taskActualPillarId && !relevantDTMPillar) { // Or if DTM is purely view-wide
              } else {
              }
              break;
            case 'map_analysis':
              if (taskActualPillarId && taskActualPillarId === currentPillarIdForAnalysis) {
              } else if (!taskActualPillarId && !currentPillarIdForAnalysis) { 
              } else {
              }
              break;
            default:
              // Unhandled data type
      }
        } else {
        }
      };
    } else {
      // Set to null or a no-op if foundational data isn't ready
      batchTaskProcessingLogicRef.current = () => {
      };
    }
  }, [
    selectedViewData, activeData, selectedPillarId, analysisPillarId, 
    refreshKPIData, refreshEventData, refreshAnalysisData, refreshACLEDData
  ]);
        
  // This is the stable handler that gets subscribed to the event bus.
  // It's memoized with an empty dependency array, so its reference never changes.
  const stableBatchTaskCompletedHandler = useCallback((data) => {
    // Always call the LATEST version of the processing logic via the ref.
    if (batchTaskProcessingLogicRef.current) {
      batchTaskProcessingLogicRef.current(data);
    }
  }, []); // Empty dependency array makes this callback stable

  // This useEffect subscribes the stable handler to the event bus.
  // Since stableBatchTaskCompletedHandler is stable, this effect runs once on mount
  // and cleans up on unmount.
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENTS.BATCH_TASK_COMPLETED, stableBatchTaskCompletedHandler);
    return () => {
      unsubscribe();
    };
  }, [stableBatchTaskCompletedHandler]); // Depends only on the stable handler

  const headerBranding = useMemo(() => ({
    title: urlParams.get('headerTitle') || localOrganizationSettings?.branding?.headerTitle || 'Situation Room AI',
    subtitle: urlParams.get('headerSubtitle') || localOrganizationSettings?.branding?.headerSubtitle || 'Humanitarian Dashboard',
    logo: normalizeUploadUrl(localOrganizationSettings?.branding?.headerLogoUrl || urlParams.get('headerLogo')) || null,
    logoStyle: urlParams.get('headerLogoStyle') || localOrganizationSettings?.branding?.headerLogoStyle || 'normal',
    disclaimerText: localOrganizationSettings?.branding?.disclaimerText || urlParams.get('headerDisclaimerText') || 'Test Environment',
    disclaimerBody: urlParams.get('headerDisclaimerBody') || 'This application is currently in testing and is for demonstration purposes only.',
    disclaimerIcon: urlParams.get('headerDisclaimerIcon') || 'WarningAmberIcon',
    statusIndicatorStyle: urlParams.get('statusIndicatorStyle') || 'rounded',
    headerColorMode: urlParams.get('headerColorMode') || 'primary',
    showStatusSchedule: urlParams.get('showStatusSchedule') === 'true',
    showDisclaimer: urlParams.get('showDisclaimer') !== 'false',
    primaryFontFamily: localOrganizationSettings?.branding?.primaryFontFamily || urlParams.get('orgPrimaryFontFamily') || urlParams.get('orgFontFamily') || undefined,
    secondaryFontFamily: localOrganizationSettings?.branding?.secondaryFontFamily || urlParams.get('orgSecondaryFontFamily') || urlParams.get('orgFontFamily') || undefined,
    fontFamily: localOrganizationSettings?.branding?.fontFamily || urlParams.get('orgFontFamily') || undefined,
    headerStyle: localOrganizationSettings?.branding?.headerStyle || urlParams.get('headerStyle') || 'full'
  }), [localOrganizationSettings?.branding, normalizeUploadUrl, urlParams]);

  return (
    <div className={`app${!showHeader ? ' no-header' : ''}`} style={{ 
      width: 'auto', 
      overflow: isLandingPage ? 'visible' : (screenSize === SCREEN_SIZES.SMALL ? 'visible' : 'hidden'),
      height: isLandingPage ? 'auto' : '100vh',
      position: 'relative'
    }}>
      <div className="app-container" style={isLandingPage ? {
        width: '100%',
        position: 'relative',
        height: 'auto'
      } : {
        width: '100%',
        height: showHeader ? 'calc(100vh - 134px)' : '100vh',
        position: 'absolute',
        top: 0,
        left: 0
      }}>
        <div className="app-content" style={isLandingPage ? { minHeight: 'auto', overflow: 'visible' } : undefined}>
      {showHeader && (
      <Header 
        availableViews={availableViews}
        selectedViewCode={selectedViewCode}
        handleViewToggle={handleViewToggle}
        organizationBranding={headerBranding}
      />
      )}

          <div className="top-section">
            <div className="controls-container">
              <div className="component-container" style={{minHeight: 173}}>
                  {/* KPI Component rendering */}
                  {/* 🚨 [DEBUG] CRITICAL: Inspect KPIComponent props for React component contamination */}
                  {(() => {
                    const kpiProps = {
                      data: orderedKpiData,
                      isLoading: isKPIDataLoading,
                      error: kpiDataError,
                      setAnimationDuration: setChartAnimationDuration,
                      animationDuration: chartAnimationDuration,
                      onBoxSelect: handleBoxClick,
                      selectedPillarId: selectedPillarId
                    };
                    
                    const reactContaminationCheckKPI = (obj, path = '') => {
                      if (!obj || typeof obj !== 'object') return;
                      
                      for (const [key, value] of Object.entries(obj)) {
                        const currentPath = path ? `${path}.${key}` : key;
                        
                        if (value && typeof value === 'object') {
                          // Check for React component signatures
                          if (value.$$typeof) {
                            console.error(`🚨 [KPI_CONTAMINATION] Found React component at ${currentPath}:`, {
                              $$typeof: value.$$typeof?.toString(),
                              type: value.type?.name || 'unknown',
                              value: value
                            });
                          }
                          
                          // Check for MapComponent specifically
                          if (value.type?.name === 'MapComponent' || 
                              (value.render && value.render.toString().includes('MapComponent'))) {
                            console.error(`🚨 [KPI_CONTAMINATION] Found MapComponent at ${currentPath}:`, value);
                          }
                          
                          // Recursively check nested objects and arrays
                          if (Array.isArray(value)) {
                            value.forEach((item, index) => {
                              if (item && typeof item === 'object') {
                                reactContaminationCheckKPI(item, `${currentPath}[${index}]`);
                              }
                            });
                          } else {
                            reactContaminationCheckKPI(value, currentPath);
                          }
                        }
                      }
                    };
                    
                    // Safety check for React contamination
                    reactContaminationCheckKPI(kpiProps, 'kpiProps');
                    
                    return null;
                  })()}
                  
                  {/* KPI Component */}
                  
                  <KPIComponent 
                    key={selectedViewCode}
                    data={orderedKpiData} 
                    isLoading={isKPIDataLoading} 
                    error={kpiDataError} 
                    setAnimationDuration={setChartAnimationDuration} 
                    animationDuration={chartAnimationDuration} 
                    onBoxSelect={handleBoxClick}
                    selectedPillarId={selectedPillarId}
                    pillarColors={localOrganizationSettings?.pillarColors || {}}
                  />
              </div>
            </div>

            {renderPanels()}
          </div>
        </div>
      </div>
      
      {selectedPillarId && selectedViewData?.id && (
        <ChatWizard 
          pillarId={selectedPillarId} 
          viewId={selectedViewData.id} 
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <SnackbarProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/landingpagedemo" element={<LandingPage />} />
            <Route path="/embed" element={<EmbedPage />} />
            <Route path="/accept-invitation" element={<InvitationAcceptance />} />
            <Route path="/brand-guidelines" element={<BrandGuidelines />} />
            <Route path="/org/:orgSlug/*" element={<ProtectedRoute>{enableProfile ? <React.Profiler id="OrganizationDashboard" onRender={profileOnRender}><OrganizationDashboard /></React.Profiler> : <OrganizationDashboard />}</ProtectedRoute>} />
            <Route path="/public/:orgSlug/*" element={enableProfile ? <React.Profiler id="OrganizationDashboard" onRender={profileOnRender}><OrganizationDashboard /></React.Profiler> : <OrganizationDashboard />} />
            <Route path="/embed/:tokenOrSlug" element={enableProfile ? <React.Profiler id="OrganizationDashboard" onRender={profileOnRender}><OrganizationDashboard /></React.Profiler> : <OrganizationDashboard />} />
            {/* Legacy public view route (temporary during migration) */}
            <Route path=":view" element={<AppContent />} />
          </Routes>
      </SnackbarProvider>
    </AuthProvider>
  );
};

export default App;